// UIManager.js - Handles all UI elements and interactions
import { parseMarkdown } from './markdownParser.js';
import { getErrorMessage } from './aiService.js';

export class UIManager {
    constructor(canvasState) {
        this.canvasState = canvasState;
        this.aiManager = null; // Set by InfiniteCanvas after construction
        this.authManager = null; // Set by InfiniteCanvas after construction
        
        // Auth UI state
        this.authModalEl = null;
        this.userMenuEl = null;
        this.signInBtnEl = null;
        this.currentAuthMode = 'signin'; // 'signin' or 'signup'
        this.nodesCreatedAsGuest = 0;
        this.lastGuestPromptTime = 0;
        this.guestPromptCooldown = 300000; // 5 minutes
        
        // Progress notification state
        this.progressNotificationTimeout = null;
        this.isProgressNotificationVisible = false;
        
        // Aspect ratio state
        this.selectedAspectRatio = '1:1'; // Default
        this.aspectRatioSelectorVisible = false;
        this.aspectRatioTimeout = null;

        // View mode & chat state
        this.currentViewMode = 'chat';
        this.chatHistory = [];
        this.chatUIInitialized = false;
        this.chatMessagesContainer = null;
        this.chatInputEl = null;
        this.chatSendBtnEl = null;
        this.chatSendBtnDefaultLabel = 'Send';
        this.chatMessageIndex = 0;
        this.chatNodeBaseX = parseFloat(localStorage.getItem('ui_chat_base_x')) || 200;
        this.chatNodeBaseY = parseFloat(localStorage.getItem('ui_chat_base_y')) || 200;
        this.chatNodeVerticalSpacing = 220;
        this.chatUserOffsetX = 420;
        this.chatAssistantOffsetX = 0;
        this.lastChatNode = null;
        this.chatBranchNodes = [];
        this.chatBranchLevels = [];
        this.chatFocusNodeId = null;
        this.chatBranchNavEl = null;
        this.chatModelToggleEl = null;
        this.chatModelInfoEl = null;
        this.chatModelModalEl = null;
        this.chatModelModalKeyHandler = null;
        this.chatAutoScroll = true;
        this.chatScrollUnlockOffset = 48;
        this.boundChatScrollHandler = null;
        this.chatScrollPositions = {};
        this.chatCurrentBranchKey = null;
        this.chatShouldScrollToTop = false;
        this.isChatLoading = false;
        this.activeChatEdit = null;
        this.modalModelList = [];
        this.chatSidebarEl = null;
        this.chatTreeContainer = null;
        this.chatTreeScrollTop = 0;
        this.chatTreeLastFocusId = null;
        this.chatTreeScrollHandlerAttached = false;
        this.chatSidebarCollapseBtn = null;
        this.chatSidebarShowBtn = null;
        this.chatContentEl = null;
        this.chatThreadExpandedState = new Set();
        this.chatSidebarCollapsed = false;
        this.chatThreadDiagnostics = null;
        this.chatThreadDiagnosticsLogKey = null;
        this.modelPromptModalEl = null;
        this.modelPromptModalModelRef = null;
        this.modelPromptModalLabel = null;
        this.modelPromptModalTitle = null;
        this.modelPromptTextarea = null;
        this.modelPromptModalKeyHandler = null;
        this.modelPromptGlobalPreviewEl = null;
        this.modelPromptCombinedPreviewEl = null;
        this.chatCanvasModeBtn = null;
        this.returnToChatBtn = null;
        this.activeCanvasThreadId = null;
        this.returnToChatHandler = null;
        this.canvasElement = null;
        this.canvasInputHandler = null;
        this.pendingCanvasFocusFrame = null;
        this.automanagedDisplayConfig = {
            baseURL: 'humos-canvas-api',
            apiKeyLabel: 'Managed by Infinite Canvas (automanaged)'
        };
        this.modalConfigMode = 'automanaged';
        this.modalActiveModels = {};
        try {
            const persistedSidebar = localStorage.getItem('ui_chat_sidebar_collapsed');
            if (persistedSidebar === 'true') {
                this.chatSidebarCollapsed = true;
            }
        } catch (storageError) {
            console.warn('Unable to read sidebar preference:', storageError);
        }

        this.setupUI();
    }
    
    // Set AIManager after it's created
    setAIManager(aiManager) {
        this.aiManager = aiManager;
        this.updateChatModelInfo();
    }

    // Set AuthManager after it's created
    setAuthManager(authManager) {
        this.authManager = authManager;
        
        // Listen for auth state changes
        if (this.authManager) {
            this.authManager.onAuthStateChange((event, session) => {
                this.handleAuthStateChange(event, session);
            });
        }
    }

    setCanvasEnvironment(canvasElement, inputHandler = null) {
        this.canvasElement = canvasElement || null;
        this.canvasInputHandler = inputHandler || null;
    }

    setupUI() {
        this.setupAuthUI();
        this.setupAPIKeyUI();
        this.setupModelSelector();
        this.setupModelPromptModal();
        this.setupContentModal();
        this.setupAspectRatioSelector();
        this.setupChatUI();
        this.returnToChatBtn = document.getElementById('returnToChatBtn');
        if (this.returnToChatBtn) {
            if (!this.returnToChatHandler) {
                this.returnToChatHandler = () => this.setViewMode('chat');
            }
            this.returnToChatBtn.removeEventListener('click', this.returnToChatHandler);
            this.returnToChatBtn.addEventListener('click', this.returnToChatHandler);
        }
        const downloadImageBtn = document.getElementById('downloadImageBtn');
        if (downloadImageBtn) {
            downloadImageBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.canvasState.selectedNodes.length === 1) {
                    const node = this.canvasState.selectedNodes[0];
                    if (node.generatedImageUrl) {
                        this.downloadNodeImage(node);
                    }
                }
            });
        }

        // Default to chat mode on initial load
        this.setViewMode('chat', { skipStorage: true });
        try {
            localStorage.setItem('ui_view_mode', 'chat');
        } catch (storageError) {
            console.warn('Unable to persist default view mode:', storageError);
        }
    }

    // API Configuration Modal
    setupAPIKeyUI() {
        if (!document.getElementById('apiKeyModal')) {
            const modal = document.createElement('div');
            modal.id = 'apiKeyModal';
            modal.className = 'api-key-modal';
            modal.innerHTML = `
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="modal-header-content">
                            <div class="ds-badge">Workspace</div>
                            <h3 class="ds-modal-title">Configuration &amp; Actions</h3>
                            <p class="ds-text-muted">Manage canvas utilities, API credentials, and available AI models.</p>
                        </div>
                        <button id="closeConfigModalBtn" class="close-modal-btn ds-icon-button" aria-label="Close configuration modal">×</button>
                    </div>

                    <div class="modal-section">
                        <h4>Canvas Actions</h4>
                        <div class="modal-action-buttons">
                            <button id="modalExportBtn" class="modal-action-btn ds-btn ds-btn--ghost">Export JSON</button>
                            <button id="modalImportBtn" class="modal-action-btn ds-btn ds-btn--ghost">Import JSON</button>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>🧠 Text Models Configuration</h4>
                        <p class="ds-text-muted">Configure AI providers, authentication, and the set of text models exposed in the canvas.</p>

                        <div class="mode-toggle-container">
                            <div class="mode-toggle-options">
                                <button type="button" class="mode-toggle-option active" data-mode="automanaged" id="automanagedToggleBtn">
                                    <div class="mode-toggle-option-content">
                                        <div class="mode-toggle-option-header">
                                            <span class="mode-toggle-icon">⚡</span>
                                            <span class="mode-toggle-title">Automanaged Mode</span>
                                            <span class="mode-toggle-badge">Default</span>
                                        </div>
                                        <p class="mode-toggle-description">Free AI with usage limits. 15 requests for guests, 20 after login. Perfect for trying the app!</p>
                                        <div id="requestLimitDisplay" class="request-limit-info"></div>
                                        <div id="automanagedModelSummary" class="automanaged-model-summary" style="display: none;">📊 Loading automanaged models…</div>
                                    </div>
                                </button>
                                <button type="button" class="mode-toggle-option" data-mode="api-keys" id="apiKeysToggleBtn">
                                    <div class="mode-toggle-option-content">
                                        <div class="mode-toggle-option-header">
                                            <span class="mode-toggle-icon">🔑</span>
                                            <span class="mode-toggle-title">API Keys Mode</span>
                                            <span class="mode-toggle-badge">Unlimited</span>
                                        </div>
                                        <p class="mode-toggle-description">Use your own API keys. Unlimited requests. Full control over providers and models.</p>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <div id="apiKeysSection" class="ds-stack-md">
                            <div class="config-subsection">
                                <h5>🔗 Text Models API Settings</h5>

                                <div class="provider-config-grid">
                                    <div class="provider-config-card">
                                        <h6>Provider 1 · OpenAI-Compatible</h6>
                                        <label for="baseUrlInputPrimary" class="ds-field-label">Base URL 1</label>
                                        <input type="text" id="baseUrlInputPrimary" class="ds-input" placeholder="https://openrouter.ai/api/v1" />

                                        <label for="apiKeyInputPrimary" class="ds-field-label">Text Models API Key 1</label>
                                        <input type="password" id="apiKeyInputPrimary" class="ds-input" placeholder="sk-... (OpenAI), or-... (OpenRouter), gsk_... (Groq)" />
                                        <small class="modal-small-print">Use any OpenAI-compatible provider such as OpenRouter, OpenAI, or Groq.</small>
                                        <small id="automanagedProviderNote" class="modal-small-print" style="display: none;">Requests are routed through humos-canvas-api and credentials are managed by Infinite Canvas.</small>
                                    </div>

                                    <!-- Gemini provider temporarily disabled -->
                                    <!--
                                    <div class="provider-config-card">
                                        <h6>Provider 2 · Google Gemini</h6>
                                        <label for="baseUrlInputSecondary" class="ds-field-label">Base URL 2</label>
                                        <input type="text" id="baseUrlInputSecondary" class="ds-input" placeholder="https://generativelanguage.googleapis.com/v1beta" />

                                        <label for="apiKeyInputSecondary" class="ds-field-label">Text Models API Key 2</label>
                                        <input type="password" id="apiKeyInputSecondary" class="ds-input" placeholder="AIza... (Gemini API Key)" />
                                        <small class="modal-small-print">Configure Gemini separately so Gemini models can run alongside OpenAI-compatible models.</small>
                                    </div>
                                    -->
                                </div>
                            </div>
                        </div>

                        <div id="modelConfigSection" class="ds-stack-md">
                            <div class="config-subsection">
                                    <div class="config-section-header">
                                        <h5>🤖 Model Configuration</h5>
                                        <div id="automanagedReadonlyBadge" class="readonly-badge" style="display: none;">
                                            <span class="readonly-badge-icon">👁️</span>
                                            <span class="readonly-badge-text">Credentials Managed (Automanaged)</span>
                                        </div>
                                    </div>

                                <label for="systemPromptInput" class="ds-field-label">System Prompt</label>
                                <textarea id="systemPromptInput" class="ds-textarea" placeholder="You are a helpful AI assistant that generates creative and relevant ideas."></textarea>
                                <small class="modal-small-print">Customize how the AI behaves when generating ideas and responses.</small>

                                <label for="aiModelsInput" class="ds-field-label">Available Text Models (comma-separated)</label>
                                <div class="ds-stack-sm">
                                    <input type="text" id="aiModelsInput" class="ds-input" placeholder="gpt-4o, claude-3-5-sonnet-20241022, llama-3.3-70b-versatile" />
                                    <button type="button" id="updateModelsBtn" class="ds-btn ds-btn--ghost">Update Model List</button>
                                </div>
                                <small class="modal-small-print">Enter model names separated by commas, then click "Update Model List" to select which ones to activate.</small>

                                <div id="modelSelectionSection" class="ds-stack-sm">
                                    <label class="ds-field-label">Select Active Text Models</label>
                                    <div id="modelCheckboxes" class="ds-scroll-container"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-buttons">
                        <button id="saveApiKeyBtn" class="ds-btn ds-btn--primary">Save Configuration</button>
                        <button id="cancelApiKeyBtn" class="ds-btn ds-btn--ghost">Close</button>
                    </div>
                    <small class="modal-small-print">
                        <strong>🧠 Text Model Providers:</strong><br>
                        • OpenAI: https://api.openai.com/v1<br>
                        • OpenRouter: https://openrouter.ai/api/v1<br>
                        • Groq: https://api.groq.com/openai/v1<br>
                        • Gemini: https://generativelanguage.googleapis.com/v1beta<br>
                        • Local models: http://localhost:1234/v1<br>
                        <br>
                        <em>Your settings are stored locally and never shared.</em>
                    </small>
                </div>
            `;
            document.body.appendChild(modal);
            
            // Add event listeners
            document.getElementById('saveApiKeyBtn').addEventListener('click', () => this.saveApiConfig());
            document.getElementById('cancelApiKeyBtn').addEventListener('click', () => this.hideApiKeyModal());
            document.getElementById('closeConfigModalBtn').addEventListener('click', () => this.hideApiKeyModal());
            
            // Mode toggle event listeners
            document.getElementById('automanagedToggleBtn').addEventListener('click', () => this.setMode('automanaged'));
            document.getElementById('apiKeysToggleBtn').addEventListener('click', () => this.setMode('api-keys'));
            
            document.getElementById('updateModelsBtn').addEventListener('click', () => this.updateModelList());
            // document.getElementById('updateImageModelsBtn').addEventListener('click', () => this.updateImageModelList());
            
            // Add listener for Replicate API key to dynamically show/hide image models section
            // document.getElementById('replicateApiKeyInput').addEventListener('input', () => {
            //     this.updateModalImageModelsVisibility();
            // });
            
            // Modal action buttons
            document.getElementById('modalExportBtn').addEventListener('click', () => {
                this.exportJSON();
                this.hideApiKeyModal();
            });
            document.getElementById('modalImportBtn').addEventListener('click', () => {
                this.importJSON();
                this.hideApiKeyModal();
            });
        }
        
        this.updateApiConfigButton();
    }
    
    showApiKeyModal() {
        if (!this.aiManager) return;
        
        const modal = document.getElementById('apiKeyModal');
        const apiKeyInputPrimary = document.getElementById('apiKeyInputPrimary');
        // const apiKeyInputSecondary = document.getElementById('apiKeyInputSecondary'); // Temporarily disabled
        const replicateApiKeyInput = document.getElementById('replicateApiKeyInput');
        const baseUrlInputPrimary = document.getElementById('baseUrlInputPrimary');
        // const baseUrlInputSecondary = document.getElementById('baseUrlInputSecondary'); // Temporarily disabled
        const aiModelsInput = document.getElementById('aiModelsInput');
        const imageModelsInput = document.getElementById('imageModelsInput');
        const systemPromptInput = document.getElementById('systemPromptInput');

        modal.style.display = 'flex';

        // Set current values
        if (apiKeyInputPrimary) {
            apiKeyInputPrimary.value = this.aiManager.apiKey || '';
        }
        // Temporarily disabled - Gemini provider
        // if (apiKeyInputSecondary) {
        //     apiKeyInputSecondary.value = this.aiManager.secondaryApiKey || '';
        // }
        if (replicateApiKeyInput) {
            replicateApiKeyInput.value = this.aiManager.replicateApiKey || '';
        }
        if (baseUrlInputPrimary) {
            baseUrlInputPrimary.value = this.aiManager.baseURL || 'https://openrouter.ai/api/v1';
        }
        // Temporarily disabled - Gemini provider
        // if (baseUrlInputSecondary) {
        //     baseUrlInputSecondary.value = this.aiManager.secondaryBaseURL || 'https://generativelanguage.googleapis.com/v1beta';
        // }
        aiModelsInput.value = this.aiManager.aiModels || 'gpt-3.5-turbo';
        if (imageModelsInput) {
            imageModelsInput.value = this.aiManager.imageModels || '';
        }
        if (systemPromptInput) {
            systemPromptInput.value = this.aiManager.systemPrompt || 'You are a helpful AI assistant that generates creative and relevant ideas.';
        }

        // Set the correct mode based on automanaged state
        this.setMode(this.aiManager.automanaged ? 'automanaged' : 'api-keys');

        this.updateModalImageModelsVisibility();
        this.updateModelList();
        this.updateImageModelList();
        
        // Update request limit display
        this.updateRequestLimitDisplay();
        this.updateAutomanagedModelSummary();
    }
    
    hideApiKeyModal() {
        document.getElementById('apiKeyModal').style.display = 'none';
    }

    setMode(mode) {
        const automanagedBtn = document.getElementById('automanagedToggleBtn');
        const apiKeysBtn = document.getElementById('apiKeysToggleBtn');
        const normalizedMode = this.normalizeModalMode(mode);
        
        if (normalizedMode === 'automanaged') {
            automanagedBtn.classList.add('active');
            apiKeysBtn.classList.remove('active');
            this.toggleAutomanaged(true);
        } else {
            automanagedBtn.classList.remove('active');
            apiKeysBtn.classList.add('active');
            this.toggleAutomanaged(false);
        }

        this.loadModalStateForMode(normalizedMode);
    }
    
    toggleAutomanaged(enabled) {
        this.toggleApiConfigVisibility(!enabled);
        if (enabled) {
            console.log('⚡ Automanaged mode enabled - using managed backend with request limits');
            this.updateRequestLimitDisplay();
        } else {
            console.log('🔑 Automanaged mode disabled - using custom API configuration');
        }
    }
    
    // Update request limit display in configuration modal
    async updateRequestLimitDisplay() {
        const displayEl = document.getElementById('requestLimitDisplay');
        if (!displayEl || !this.aiManager) return;
        
        if (!this.aiManager.automanaged) {
            displayEl.innerHTML = '';
            return;
        }
        
        try {
            const limits = await this.aiManager.checkRequestLimits();
            const isGuest = !this.aiManager.canvasState.authManager?.currentUser;
            const statusClass = limits.remaining > 5 ? 'request-ok' : limits.remaining > 0 ? 'request-warning' : 'request-depleted';
            
            displayEl.innerHTML = `
                <span class="${statusClass}">
                    📊 ${limits.remaining} of ${limits.limit} requests remaining
                    ${isGuest ? '(Guest - Login for more!)' : '(Authenticated)'}
                </span>
            `;
        } catch (error) {
            console.error('Failed to update request limit display:', error);
            displayEl.innerHTML = '<span style="color: #999;">Request limits unavailable</span>';
        }
    }

    updateAutomanagedModelSummary(models = null, defaultModelId = null) {
        const summaryEl = document.getElementById('automanagedModelSummary');
        if (!summaryEl) {
            return;
        }

        const isAutomanagedView = this.modalConfigMode === 'automanaged';
        if (!isAutomanagedView) {
            summaryEl.style.display = 'none';
            summaryEl.textContent = '📊 Loading automanaged models…';
            return;
        }

        const normalizeId = (entry) => {
            if (!entry) return '';
            if (typeof entry === 'string') return entry.trim();
            if (typeof entry.id === 'string') return entry.id.trim();
            return '';
        };

        let modelIds = [];

        if (Array.isArray(models) && models.length > 0) {
            modelIds = models.map(normalizeId).filter(Boolean);
        } else if (Array.isArray(this.aiManager?.automanagedFetchedModels)) {
            modelIds = this.aiManager.automanagedFetchedModels.map(normalizeId).filter(Boolean);
        } else if (Array.isArray(this.aiManager?.aiModels)) {
            modelIds = this.aiManager.aiModels.map(normalizeId).filter(Boolean);
        } else if (typeof this.aiManager?.aiModels === 'string') {
            modelIds = this.aiManager.aiModels
                .split(',')
                .map(name => name.trim())
                .filter(Boolean);
        }

        if (modelIds.length === 0) {
            summaryEl.style.display = 'block';
            summaryEl.textContent = '📊 Loading automanaged models…';
            return;
        }

        const resolvedDefault = (defaultModelId && modelIds.includes(defaultModelId))
            ? defaultModelId
            : modelIds[0];

        const orderedIds = [
            resolvedDefault,
            ...modelIds.filter(id => id !== resolvedDefault)
        ];

        const lines = [
            `📊 Available models: ${modelIds.length}`,
            ` ${resolvedDefault}`
        ];

        const otherModels = orderedIds.slice(1).map(id => `      ${id}`);
        if (otherModels.length > 0) {
            lines.push('');
            lines.push(...otherModels);
        }

        summaryEl.style.display = 'block';
        summaryEl.textContent = lines.join('\n');
    }
    
    // Show modal when request limit is reached
    showRequestLimitReached(isGuest) {
        if (isGuest) {
            this.showNotification(
                '🔒 You\'ve used your 15 free requests. Please login for 20 more!',
                'warning',
                0 // Stay until dismissed
            );
            
            // Show auth modal if available
            if (this.canvasState.authManager && this.canvasState.authManager.isConfigured()) {
                setTimeout(() => {
                    this.showAuthModal();
                }, 1000);
            }
        } else {
            this.showNotification(
                '🎯 You\'ve reached your 20 free requests. Add your own API keys in ⚙️ Configure for unlimited access!',
                'warning',
                0 // Stay until dismissed
            );
            
            // Optionally show config modal
            setTimeout(() => {
                this.showApiKeyModal();
            }, 2000);
        }
    }

    toggleApiConfigVisibility(isApiKeyMode) {
        // Keep API credentials visible but adjust interactivity per mode
        const apiKeysSection = document.getElementById('apiKeysSection');
        if (apiKeysSection) {
            apiKeysSection.style.display = 'block';
            if (isApiKeyMode) {
                apiKeysSection.classList.remove('automanaged-readonly');
            } else {
                apiKeysSection.classList.add('automanaged-readonly');
            }
        }
        
        // Show/hide readonly badge
        const readonlyBadge = document.getElementById('automanagedReadonlyBadge');
        if (readonlyBadge) {
            readonlyBadge.style.display = isApiKeyMode ? 'none' : 'inline-flex';
        }

        const providerNote = document.getElementById('automanagedProviderNote');
        if (providerNote) {
            providerNote.style.display = isApiKeyMode ? 'none' : 'block';
        }

        this.updateAutomanagedProviderInputs(isApiKeyMode);
        
        // Model configuration section is always visible
        const modelConfigSection = document.getElementById('modelConfigSection');
        if (modelConfigSection) {
            modelConfigSection.style.display = 'block'; // Always visible
            
            const systemPromptInput = document.getElementById('systemPromptInput');
            const aiModelsInput = document.getElementById('aiModelsInput');
            const updateModelsBtn = document.getElementById('updateModelsBtn');
            const modelCheckboxes = document.querySelectorAll('#modelCheckboxes input[type="checkbox"]');
            const modelOrderButtons = document.querySelectorAll('#modelCheckboxes button');
            
            if (systemPromptInput) {
                systemPromptInput.disabled = false;
                systemPromptInput.style.opacity = '1';
                systemPromptInput.style.cursor = 'text';
            }
            
            if (aiModelsInput) {
                aiModelsInput.disabled = false;
                aiModelsInput.style.opacity = '1';
                aiModelsInput.style.cursor = 'text';
            }
            
            if (updateModelsBtn) {
                updateModelsBtn.disabled = false;
                updateModelsBtn.style.opacity = '1';
                updateModelsBtn.style.cursor = 'pointer';
            }
            
            modelCheckboxes.forEach(checkbox => {
                checkbox.disabled = false;
                checkbox.style.cursor = 'pointer';
            });
            
            modelOrderButtons.forEach(button => {
                button.disabled = false;
                button.style.cursor = 'pointer';
            });
        }
    }

    normalizeModalMode(mode) {
        return mode === 'automanaged' ? 'automanaged' : 'api-keys';
    }

    getManagerModeKey(mode) {
        return mode === 'automanaged' ? 'automanaged' : 'api';
    }

    isEditingActiveMode() {
        if (!this.aiManager) return false;
        const activeMode = this.aiManager.automanaged ? 'automanaged' : 'api-keys';
        return this.modalConfigMode === activeMode;
    }

    loadModalStateForMode(mode) {
        if (!this.aiManager) return;

        const displayMode = this.normalizeModalMode(mode);
        this.modalConfigMode = displayMode;
        const managerMode = this.getManagerModeKey(displayMode);

        const config = this.aiManager.getStoredModeConfig(managerMode);
        const aiModelsInput = document.getElementById('aiModelsInput');
        const systemPromptInput = document.getElementById('systemPromptInput');
        const baseUrlInputPrimary = document.getElementById('baseUrlInputPrimary');
        const apiKeyInputPrimary = document.getElementById('apiKeyInputPrimary');

        if (systemPromptInput) {
            const fallbackPrompt = typeof this.aiManager.getDefaultSystemPrompt === 'function'
                ? this.aiManager.getDefaultSystemPrompt()
                : 'You are a helpful AI assistant that generates creative and relevant ideas.';
            const promptValue = typeof config.systemPrompt === 'string' && config.systemPrompt.trim().length > 0
                ? config.systemPrompt
                : fallbackPrompt;
            systemPromptInput.value = promptValue;
        }

        if (aiModelsInput) {
            aiModelsInput.value = typeof config.aiModels === 'string' ? config.aiModels : '';
        }

        if (displayMode === 'api-keys' && baseUrlInputPrimary && apiKeyInputPrimary && this.aiManager) {
            baseUrlInputPrimary.value = this.aiManager.baseURL || '';
            apiKeyInputPrimary.value = this.aiManager.apiKey || '';
        }

        const normalizedModels = Array.isArray(config.aiModels)
            ? config.aiModels
            : this.aiManager.normalizeModelList
                ? this.aiManager.normalizeModelList(config.aiModels)
                : (typeof config.aiModels === 'string'
                    ? config.aiModels.split(',').map(m => m.trim()).filter(Boolean)
                    : []);

        this.modalModelList = normalizedModels;
        this.modalActiveModels = config.activeModels && typeof config.activeModels === 'object'
            ? { ...config.activeModels }
            : {};

        this.renderModalModelList();
        this.syncModalModelsToInput();
        this.updateAutomanagedModelSummary();
    }

    isModalModelActive(reference) {
        if (!this.aiManager) return true;
        const map = this.modalActiveModels && typeof this.modalActiveModels === 'object'
            ? this.modalActiveModels
            : {};
        const { key, name } = this.aiManager.getModelReferenceParts(reference);

        if (key && Object.prototype.hasOwnProperty.call(map, key)) {
            return map[key] !== false;
        }

        if (name && Object.prototype.hasOwnProperty.call(map, name)) {
            return map[name] !== false;
        }

        return true;
    }

    updateModalActiveState(reference, isActive, options = {}) {
        if (!this.aiManager) return;
        const { skipPanelUpdate = false } = options || {};

        if (!this.modalActiveModels || typeof this.modalActiveModels !== 'object') {
            this.modalActiveModels = {};
        }

        const { key, name } = this.aiManager.getModelReferenceParts(reference);
        const storageKey = key || name;
        if (!storageKey) {
            return;
        }

        this.modalActiveModels[storageKey] = isActive;

        if (key && name && key !== name && Object.prototype.hasOwnProperty.call(this.modalActiveModels, name)) {
            delete this.modalActiveModels[name];
        }

        if (this.isEditingActiveMode()) {
            this.aiManager.setModelActive(reference, isActive);
            if (!skipPanelUpdate) {
                this.updateModelSelectorUI();
            }
        }
    }

    setAllModalModelsActive(select) {
        const descriptors = this.getModalModelDescriptors();
        if (descriptors.length === 0) {
            return;
        }

        descriptors.forEach((descriptor) => {
            this.updateModalActiveState(descriptor, select, { skipPanelUpdate: true });
        });

        if (this.isEditingActiveMode()) {
            this.updateModelSelectorUI();
        }

        this.renderModalModelList();
    }

    updateAutomanagedProviderInputs(isApiKeyMode) {
        const baseUrlInputPrimary = document.getElementById('baseUrlInputPrimary');
        const apiKeyInputPrimary = document.getElementById('apiKeyInputPrimary');

        if (isApiKeyMode) {
            this.restoreAutomanagedProviderState(baseUrlInputPrimary);
            this.restoreAutomanagedProviderState(apiKeyInputPrimary);
        } else {
            const baseLabel = this.automanagedDisplayConfig?.baseURL || 'humos-canvas-api';
            const keyLabel = this.automanagedDisplayConfig?.apiKeyLabel || 'Managed by Infinite Canvas';
            this.applyAutomanagedProviderState(
                baseUrlInputPrimary,
                baseLabel,
                'The Infinite Canvas backend routes requests through this base URL.'
            );
            this.applyAutomanagedProviderState(
                apiKeyInputPrimary,
                keyLabel,
                'Credentials are stored securely on the Infinite Canvas backend.'
            );
        }
    }

    applyAutomanagedProviderState(input, displayValue, tooltip) {
        if (!input) {
            return;
        }

        if (input.dataset.automanagedReadonly === 'true') {
            input.value = displayValue;
            return;
        }

        input.dataset.automanagedReadonly = 'true';
        input.dataset.originalValue = input.value || '';
        input.dataset.originalOpacity = input.style.opacity || '';
        input.dataset.originalCursor = input.style.cursor || '';
        input.dataset.originalTitle = input.title || '';
        if (!input.dataset.originalType) {
            input.dataset.originalType = input.type || 'text';
        }

        input.value = displayValue;
        input.type = 'text';
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        input.title = tooltip || 'Automanaged mode controls this setting.';
        input.style.opacity = '0.7';
        input.style.cursor = 'not-allowed';
    }

    restoreAutomanagedProviderState(input) {
        if (!input) {
            return;
        }

        if (input.dataset.automanagedReadonly !== 'true') {
            return;
        }

        const originalType = input.dataset.originalType || 'text';
        input.type = originalType;
        input.readOnly = false;
        input.removeAttribute('aria-readonly');
        input.title = input.dataset.originalTitle || '';
        input.value = input.dataset.originalValue || '';
        input.style.opacity = input.dataset.originalOpacity || '';
        input.style.cursor = input.dataset.originalCursor || '';

        delete input.dataset.automanagedReadonly;
        delete input.dataset.originalValue;
        delete input.dataset.originalOpacity;
        delete input.dataset.originalCursor;
        delete input.dataset.originalType;
        delete input.dataset.originalTitle;
    }

    updateModalImageModelsVisibility() {
        const replicateApiKeyInput = document.getElementById('replicateApiKeyInput');
        const imageModelInputs = document.querySelector('[data-section="image-model-inputs"]');
        
        if (!replicateApiKeyInput || !imageModelInputs) return;
        
        const hasReplicateKey = replicateApiKeyInput.value.trim() !== '';
        imageModelInputs.style.display = hasReplicateKey ? 'block' : 'none';
        
        // Also update the model selector UI when API key changes
        if (this.aiManager) {
            this.aiManager.replicateApiKey = replicateApiKeyInput.value.trim();
            this.updateModelSelectorUI();
        }
    }

    buildModelDescriptors(models = []) {
        if (!Array.isArray(models) || models.length === 0) {
            return [];
        }

        const normalized = models
            .map(model => (typeof model === 'string' ? model.trim() : ''))
            .filter(model => model.length > 0);

        if (normalized.length === 0) {
            return [];
        }

        const totals = normalized.reduce((acc, model) => {
            acc[model] = (acc[model] || 0) + 1;
            return acc;
        }, {});

        const seen = {};

        return normalized.map((model, index) => {
            seen[model] = (seen[model] || 0) + 1;
            const occurrence = seen[model];
            const total = totals[model];
            const label = total > 1 ? `${model} #${occurrence}` : model;

            return {
                name: model,
                index,
                key: `${model}__${index}`,
                label,
                occurrence,
                totalOccurrences: total
            };
        });
    }

    normalizeModelPromptReference(descriptor, labelOverride = null) {
        if (!descriptor) {
            return null;
        }

        if (typeof descriptor === 'string') {
            const name = descriptor.trim();
            if (!name) {
                return null;
            }
            return {
                name,
                key: `${name}__0`,
                label: labelOverride || name
            };
        }

        if (typeof descriptor === 'object') {
            const name = typeof descriptor.name === 'string' ? descriptor.name.trim() : '';
            const key = typeof descriptor.key === 'string' ? descriptor.key.trim() : '';
            if (!name && !key) {
                return null;
            }

            return {
                name: name || key,
                key: key || (name ? `${name}__0` : ''),
                label: labelOverride || descriptor.label || name || key
            };
        }

        return null;
    }

    sanitizeDomId(value, fallback = '') {
        const base = typeof value === 'string' ? value : '';
        const sanitized = base.replace(/[^a-zA-Z0-9_-]+/g, '_');
        if (sanitized.length > 0) {
            return sanitized;
        }

        const fallbackSanitized = typeof fallback === 'string' ? fallback.replace(/[^a-zA-Z0-9_-]+/g, '_') : '';
        if (fallbackSanitized.length > 0) {
            return fallbackSanitized;
        }

        return `id_${Math.random().toString(36).slice(2, 8)}`;
    }

    getModalModelDescriptors() {
        return this.buildModelDescriptors(this.modalModelList);
    }

    syncModalModelsToInput() {
        const aiModelsInput = document.getElementById('aiModelsInput');
        if (!aiModelsInput) return;

        if (Array.isArray(this.modalModelList) && this.modalModelList.length > 0) {
            aiModelsInput.value = this.modalModelList.join(', ');
        } else {
            aiModelsInput.value = '';
        }
    }

    updateModelList() {
        if (!this.aiManager) return;
        
        const aiModelsInput = document.getElementById('aiModelsInput');
        const modelCheckboxes = document.getElementById('modelCheckboxes');

        if (!aiModelsInput || !modelCheckboxes) return;

        const modelsText = aiModelsInput.value.trim();
        if (!modelsText) {
            modelCheckboxes.innerHTML = '<p class="modal-empty-state">Enter models above to see selection options.</p>';
            return;
        }

        const models = modelsText.split(',').map(m => m.trim()).filter(m => m.length > 0);

        this.modalModelList = models;
        this.renderModalModelList();
    }

    renderModalModelList() {
        const modelCheckboxes = document.getElementById('modelCheckboxes');
        if (!modelCheckboxes) return;

        const descriptors = this.getModalModelDescriptors();

        if (descriptors.length === 0) {
            modelCheckboxes.innerHTML = '<p class="modal-empty-state">No valid models found.</p>';
            this.syncModalModelsToInput();
            return;
        }

        modelCheckboxes.innerHTML = '';

        descriptors.forEach((descriptor, index) => {
            const reference = this.normalizeModelPromptReference(descriptor, descriptor.label);
            if (!reference) {
                return;
            }

            const isActive = this.isModalModelActive(reference);

            const row = document.createElement('div');
            row.className = 'modal-model-row';

            const reorderWrapper = document.createElement('div');
            reorderWrapper.className = 'modal-model-reorder';

            const createOrderButton = (symbol, disabled) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.textContent = symbol;
                btn.title = symbol === '↑' ? 'Move up' : 'Move down';
                btn.className = 'modal-model-order-btn';
                btn.disabled = disabled;
                return btn;
            };

            const moveUpBtn = createOrderButton('↑', index === 0);
            const moveDownBtn = createOrderButton('↓', index === descriptors.length - 1);

            moveUpBtn.addEventListener('click', () => {
                this.reorderModalModel(index, index - 1);
            });

            moveDownBtn.addEventListener('click', () => {
                this.reorderModalModel(index, index + 1);
            });

            reorderWrapper.appendChild(moveUpBtn);
            reorderWrapper.appendChild(moveDownBtn);

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = `model_${this.sanitizeDomId(reference.key || `${reference.name}_${index}`, `model_${index}`)}`;
            checkbox.checked = isActive;
            checkbox.className = 'modal-model-checkbox';

            const label = document.createElement('label');
            label.htmlFor = checkbox.id;
            label.textContent = reference.label;
            label.className = 'modal-model-label';

            checkbox.addEventListener('change', () => {
                this.updateModalActiveState(reference, checkbox.checked);
            });

            row.appendChild(reorderWrapper);
            row.appendChild(checkbox);
            row.appendChild(label);
            modelCheckboxes.appendChild(row);
        });

        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-model-actions';

        const selectAllBtn = document.createElement('button');
        selectAllBtn.textContent = 'Select All';
        selectAllBtn.className = 'modal-model-action-btn';
        selectAllBtn.addEventListener('click', () => this.selectAllModels(true));

        const selectNoneBtn = document.createElement('button');
        selectNoneBtn.textContent = 'Select None';
        selectNoneBtn.className = 'modal-model-action-btn';
        selectNoneBtn.addEventListener('click', () => this.selectAllModels(false));

        buttonContainer.appendChild(selectAllBtn);
        buttonContainer.appendChild(selectNoneBtn);
        modelCheckboxes.appendChild(buttonContainer);

        this.syncModalModelsToInput();
    }

    reorderModalModel(fromIndex, toIndex) {
        if (!Array.isArray(this.modalModelList)) return;
        if (toIndex < 0 || toIndex >= this.modalModelList.length) return;

        const [moved] = this.modalModelList.splice(fromIndex, 1);
        this.modalModelList.splice(toIndex, 0, moved);
        this.renderModalModelList();
    }

    updateImageModelList() {
        if (!this.aiManager) return;
        
        const imageModelsInput = document.getElementById('imageModelsInput');
        const imageModelCheckboxes = document.getElementById('imageModelCheckboxes');

        if (!imageModelsInput || !imageModelCheckboxes) return;

        const modelsText = imageModelsInput.value.trim();
        if (!modelsText) {
            imageModelCheckboxes.innerHTML = '<p class="modal-empty-state">Enter image models above to see selection options.</p>';
            return;
        }

        const models = modelsText.split(',').map(m => m.trim()).filter(m => m.length > 0);

        if (models.length === 0) {
            imageModelCheckboxes.innerHTML = '<p class="modal-empty-state">No valid image models found.</p>';
            return;
        }

        imageModelCheckboxes.innerHTML = '';
        models.forEach((model, index) => {
            const isActive = this.aiManager.activeImageModels[model] !== false;

            const checkboxContainer = document.createElement('div');
            checkboxContainer.className = 'modal-model-row';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = `imageModel_${index}`;
            checkbox.checked = isActive;
            checkbox.className = 'modal-model-checkbox';

            const label = document.createElement('label');
            label.htmlFor = `imageModel_${index}`;
            label.textContent = model;
            label.className = 'modal-model-label';

            checkbox.addEventListener('change', () => {
                this.aiManager.activeImageModels[model] = checkbox.checked;
            });

            checkboxContainer.appendChild(checkbox);
            checkboxContainer.appendChild(label);
            imageModelCheckboxes.appendChild(checkboxContainer);
        });

        // Add select all/none buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-model-actions';

        const selectAllBtn = document.createElement('button');
        selectAllBtn.textContent = 'Select All';
        selectAllBtn.className = 'modal-model-action-btn';
        selectAllBtn.addEventListener('click', () => this.selectAllImageModels(true));

        const selectNoneBtn = document.createElement('button');
        selectNoneBtn.textContent = 'Select None';
        selectNoneBtn.className = 'modal-model-action-btn';
        selectNoneBtn.addEventListener('click', () => this.selectAllImageModels(false));

        buttonContainer.appendChild(selectAllBtn);
        buttonContainer.appendChild(selectNoneBtn);
        imageModelCheckboxes.appendChild(buttonContainer);
        
        this.updateImageModelSelectorUI();
    }

    selectAllModels(select) {
        if (!this.aiManager) return;
        this.setAllModalModelsActive(select);
    }
    
    selectAllImageModels(select) {
        if (!this.aiManager) return;
        
        // Update modal checkboxes if they exist
        const imageModelCheckboxes = document.getElementById('imageModelCheckboxes');
        if (imageModelCheckboxes) {
            const modalCheckboxes = imageModelCheckboxes.querySelectorAll('input[type="checkbox"]');
            modalCheckboxes.forEach(checkbox => {
                checkbox.checked = select;
                checkbox.dispatchEvent(new Event('change'));
            });
        }

        // Update persistent panel checkboxes for image models
        const modelContainer = document.getElementById('modelCheckboxesContainer');
        if (modelContainer) {
            const imageCheckboxes = modelContainer.querySelectorAll('input[id^="image_"]');
            imageCheckboxes.forEach(checkbox => {
                checkbox.checked = select;
                checkbox.dispatchEvent(new Event('change'));
            });
        }
    }
    
    saveApiConfig() {
        if (!this.aiManager) return;
        
        const automanagedBtn = document.getElementById('automanagedToggleBtn');
        const automanaged = automanagedBtn && automanagedBtn.classList.contains('active');

        const systemPromptInput = document.getElementById('systemPromptInput');
        const systemPrompt = systemPromptInput ? systemPromptInput.value.trim() : 'You are a helpful AI assistant that generates creative and relevant ideas.';
        const managerModeKey = this.getManagerModeKey(this.modalConfigMode);
        const storedModeConfig = this.aiManager ? this.aiManager.getStoredModeConfig(managerModeKey) : null;
        const modeModelPrompts = storedModeConfig?.modelSystemPrompts || {};

        if (automanaged) {
            // Automanaged mode - keep model list but use backend for generation
            const aiModelsInput = document.getElementById('aiModelsInput');
            const aiModels = aiModelsInput.value.trim();
            
            this.aiManager.saveApiConfig({
                automanaged: true,
                textProviders: {
                    primary: { apiKey: null, baseURL: null },
                    secondary: { apiKey: null, baseURL: null }
                },
                replicateApiKey: null,
                aiModels: aiModels, // Preserve user's model list
                activeModels: { ...(this.modalActiveModels || {}) }, // Preserve active selections
                imageModels: this.aiManager.imageModels,
                activeImageModels: this.aiManager.activeImageModels,
                systemPrompt,
                modelSystemPrompts: modeModelPrompts
            });

            this.hideApiKeyModal();
            this.updateApiConfigButton();
            this.updateModelSelectorUI();
            this.updateImageModelSelectorUI();

            this.showNotification(`⚡ Automanaged mode enabled - free AI with request limits!`, 'success');
        } else {
            // Regular API mode
            const apiKeyInputPrimary = document.getElementById('apiKeyInputPrimary');
            // const apiKeyInputSecondary = document.getElementById('apiKeyInputSecondary'); // Temporarily disabled
            const replicateApiKeyInput = document.getElementById('replicateApiKeyInput');
            const baseUrlInputPrimary = document.getElementById('baseUrlInputPrimary');
            // const baseUrlInputSecondary = document.getElementById('baseUrlInputSecondary'); // Temporarily disabled
            const aiModelsInput = document.getElementById('aiModelsInput');
            const imageModelsInput = document.getElementById('imageModelsInput');
            
            const primaryApiKey = apiKeyInputPrimary ? apiKeyInputPrimary.value.trim() : '';
            // const secondaryApiKey = apiKeyInputSecondary ? apiKeyInputSecondary.value.trim() : ''; // Temporarily disabled
            const replicateApiKey = replicateApiKeyInput ? replicateApiKeyInput.value.trim() : '';
            const primaryBaseURL = baseUrlInputPrimary ? baseUrlInputPrimary.value.trim() : '';
            // const secondaryBaseURL = baseUrlInputSecondary ? baseUrlInputSecondary.value.trim() : ''; // Temporarily disabled
            const aiModels = aiModelsInput.value.trim();
            const imageModels = imageModelsInput ? imageModelsInput.value.trim() : '';

            const hasPrimaryProvider = primaryApiKey && primaryBaseURL;
            // const hasSecondaryProvider = secondaryApiKey && secondaryBaseURL; // Temporarily disabled

            if (primaryApiKey && !primaryBaseURL) {
                this.showNotification('Please enter a base URL for Provider 1', 'error');
                return;
            }

            if (primaryBaseURL && !primaryApiKey) {
                this.showNotification('Please enter an API key for Provider 1', 'error');
                return;
            }

            // Temporarily disabled - Gemini provider validation
            // if (secondaryApiKey && !secondaryBaseURL) {
            //     this.showNotification('Please enter a base URL for Provider 2 (Gemini)', 'error');
            //     return;
            // }

            // if (secondaryBaseURL && !secondaryApiKey) {
            //     this.showNotification('Please enter an API key for Provider 2 (Gemini)', 'error');
            //     return;
            // }

            if (!hasPrimaryProvider) {
                this.showNotification('Please configure the text model provider.', 'error');
                return;
            }

            if (!aiModels) {
                this.showNotification('Please enter at least one AI model', 'error');
                return;
            }

            try {
                if (hasPrimaryProvider) {
                    new URL(primaryBaseURL);
                }
                // Temporarily disabled - Gemini provider URL validation
                // if (hasSecondaryProvider) {
                //     new URL(secondaryBaseURL);
                // }
            } catch (e) {
                this.showNotification('Please enter a valid URL', 'error');
                return;
            }

            this.aiManager.saveApiConfig({
                automanaged: false,
                textProviders: {
                    primary: { apiKey: hasPrimaryProvider ? primaryApiKey : '', baseURL: hasPrimaryProvider ? primaryBaseURL : '' },
                    secondary: { apiKey: '', baseURL: '' } // Temporarily disabled - Gemini provider
                },
                replicateApiKey,
                aiModels,
                activeModels: { ...(this.modalActiveModels || {}) },
                imageModels,
                activeImageModels: this.aiManager.activeImageModels,
                systemPrompt,
                modelSystemPrompts: modeModelPrompts
            });

            this.hideApiKeyModal();
            this.updateApiConfigButton();
            this.updateModelSelectorUI();
            this.updateImageModelSelectorUI();

            const provider = this.aiManager.getProviderName();
            const modelCount = aiModels.split(',').length;
            this.showNotification(`${provider} configuration saved with ${modelCount} model(s)!`, 'success');
        }

        const normalizedList = (this.aiManager.aiModels || '')
            .split(',')
            .map(model => model.trim())
            .filter(model => model.length > 0);
        this.modalModelList = normalizedList;
        this.modalActiveModels = { ...(this.aiManager.activeModels || {}) };
    }
    
    updateApiConfigButton() {
        if (!this.aiManager) return;
        
        const btn = document.getElementById('configureBtn');
        if (!btn) return;
        
        if (this.aiManager.automanaged) {
            btn.textContent = '⚙️ Configure (Automanaged)';
            btn.title = 'Automanaged mode is active with free AI and request limits. Click to configure.';
            btn.style.background = '#17a2b8';
        } else if (this.aiManager.hasAnyTextProvider()) {
            const provider = this.aiManager.getProviderName();
            btn.textContent = `⚙️ Configure (${provider})`;
            btn.title = `API configured for ${provider}. Click to configure.`;
            btn.style.background = '#28a745';
        } else {
            btn.textContent = '⚙️ Configure';
            btn.title = 'Configure AI API settings and canvas actions';
            btn.style.background = '#dc3545';
        }
    }

    // Model Selector Panel
    setupModelSelector() {
        const modelPanel = document.querySelector('.model-selector-panel');
        const toggleBtn = document.getElementById('toggleModelsBtn');
        const panelHeader = document.getElementById('modelPanelHeader');
        const modelContainer = document.getElementById('modelCheckboxesContainer');
        const dragHandle = document.getElementById('dragHandle');

        if (!modelPanel || !toggleBtn || !modelContainer || !dragHandle) {
            console.error('Model selector elements not found');
            return;
        }

        this.loadPanelPosition(modelPanel);

        let isCollapsed = localStorage.getItem('model_panel_collapsed') === 'true';
        let lastPanelDragTime = 0;
        
        const updatePanelState = () => {
            if (isCollapsed) {
                modelContainer.classList.add('collapsed');
                toggleBtn.textContent = '▼';
                toggleBtn.title = 'Show Models';
            } else {
                modelContainer.classList.remove('collapsed');
                toggleBtn.textContent = '▲';
                toggleBtn.title = 'Hide Models';
            }
        };

        updatePanelState();

        const togglePanel = () => {
            isCollapsed = !isCollapsed;
            localStorage.setItem('model_panel_collapsed', isCollapsed.toString());
            updatePanelState();
        };

        toggleBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            togglePanel();
        });

        panelHeader.addEventListener('click', (event) => {
            if (Date.now() - lastPanelDragTime < 200) return;
            togglePanel();
        });

        this.setupPanelDrag(modelPanel, dragHandle, (wasDragged) => {
            if (wasDragged) {
                lastPanelDragTime = Date.now();
            }
        });

        window.addEventListener('resize', () => {
            this.constrainPanelToBounds(modelPanel);
        });

        this.updateModelSelectorUI();
    }

    setupModelPromptModal() {
        if (this.modelPromptModalEl) {
            return;
        }

        let modal = document.getElementById('modelPromptModal');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'modelPromptModal';
            modal.className = 'model-prompt-modal hidden';
            modal.innerHTML = `
                <div class="modal-backdrop"></div>
                <div class="model-prompt-modal-container" role="dialog" aria-modal="true" aria-labelledby="modelPromptModalTitle">
                    <div class="model-prompt-modal-header">
                        <h3 id="modelPromptModalTitle">Configure Model Prompt</h3>
                        <button type="button" class="close-modal-btn" id="closeModelPromptModalBtn" aria-label="Close">×</button>
                    </div>
                    <div class="model-prompt-modal-body">
                        <p class="model-prompt-description">Model-specific prompts run <strong>after</strong> your global system prompt. Use them to fine-tune individual models.</p>
                        <div class="model-prompt-preview">
                            <label class="ds-field-label">Global Prompt</label>
                            <div id="modelPromptGlobalPreview" class="model-prompt-preview-box is-empty">No global prompt configured.</div>
                        </div>
                        <label for="modelPromptTextarea" class="ds-field-label">Model Prompt</label>
                        <textarea id="modelPromptTextarea" class="ds-textarea" rows="6" placeholder="Add extra instructions for this model (optional)"></textarea>
                        <small class="modal-small-print">Leave blank to inherit only the global system prompt.</small>
                        <div class="model-prompt-preview">
                            <label class="ds-field-label">Combined Prompt Preview</label>
                            <div id="modelPromptCombinedPreview" class="model-prompt-preview-box is-empty">Model will only use the global prompt.</div>
                        </div>
                    </div>
                    <div class="model-prompt-modal-footer">
                        <button type="button" class="ds-btn ds-btn--ghost" id="clearModelPromptBtn">Clear</button>
                        <div class="model-prompt-footer-spacer"></div>
                        <button type="button" class="ds-btn ds-btn--ghost" id="cancelModelPromptBtn">Cancel</button>
                        <button type="button" class="ds-btn ds-btn--primary" id="saveModelPromptBtn">Save Prompt</button>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);
        }

        this.modelPromptModalEl = modal;
        this.modelPromptTextarea = modal.querySelector('#modelPromptTextarea');
        this.modelPromptModalTitle = modal.querySelector('#modelPromptModalTitle');
        this.modelPromptGlobalPreviewEl = modal.querySelector('#modelPromptGlobalPreview');
        this.modelPromptCombinedPreviewEl = modal.querySelector('#modelPromptCombinedPreview');

        const closeBtn = modal.querySelector('#closeModelPromptModalBtn');
        const cancelBtn = modal.querySelector('#cancelModelPromptBtn');
        const saveBtn = modal.querySelector('#saveModelPromptBtn');
        const clearBtn = modal.querySelector('#clearModelPromptBtn');
        const backdrop = modal.querySelector('.modal-backdrop');

        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.hideModelPromptModal());
        }

        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.hideModelPromptModal());
        }

        if (backdrop) {
            backdrop.addEventListener('click', () => this.hideModelPromptModal());
        }

        if (saveBtn) {
            saveBtn.addEventListener('click', () => this.handleModelPromptSave());
        }

        if (clearBtn) {
            clearBtn.addEventListener('click', () => this.handleModelPromptClear());
        }

        if (this.modelPromptTextarea) {
            this.modelPromptTextarea.addEventListener('input', () => {
                this.autosizeTextarea(this.modelPromptTextarea, 10);
                this.updateModelPromptPreview();
            });
        }
    }

    showModelPromptModal(descriptor, label = null) {
        if (!this.aiManager) {
            return;
        }

        const reference = this.normalizeModelPromptReference(descriptor, label);
        if (!reference) {
            return;
        }

        this.setupModelPromptModal();

        if (!this.modelPromptModalEl) {
            return;
        }

        this.modelPromptModalModelRef = reference;
        this.modelPromptModalLabel = reference.label || reference.name;

        const existingPrompt = this.aiManager.getModelSystemPrompt(reference);
        if (this.modelPromptTextarea) {
            this.modelPromptTextarea.value = existingPrompt;
            this.autosizeTextarea(this.modelPromptTextarea, 10);
            try {
                this.modelPromptTextarea.focus({ preventScroll: true });
            } catch (focusError) {
                this.modelPromptTextarea.focus();
            }
        }

        if (this.modelPromptModalTitle) {
            this.modelPromptModalTitle.textContent = `Configure Prompt – ${this.modelPromptModalLabel}`;
        }

        this.updateModelPromptPreview();

        this.modelPromptModalEl.classList.remove('hidden');

        if (!this.modelPromptModalKeyHandler) {
            this.modelPromptModalKeyHandler = (event) => {
                if (event.key === 'Escape') {
                    this.hideModelPromptModal();
                }
            };
            document.addEventListener('keydown', this.modelPromptModalKeyHandler);
        }
    }

    hideModelPromptModal() {
        if (!this.modelPromptModalEl) {
            return;
        }

        if (this.modelPromptModalEl.classList.contains('hidden')) {
            return;
        }

        this.modelPromptModalEl.classList.add('hidden');
        this.modelPromptModalModelRef = null;
        this.modelPromptModalLabel = null;

        if (this.modelPromptTextarea) {
            this.modelPromptTextarea.value = '';
            this.autosizeTextarea(this.modelPromptTextarea, 10);
        }

        this.updateModelPromptPreview();

        if (this.modelPromptModalKeyHandler) {
            document.removeEventListener('keydown', this.modelPromptModalKeyHandler);
            this.modelPromptModalKeyHandler = null;
        }
    }

    handleModelPromptSave() {
        if (!this.aiManager || !this.modelPromptModalModelRef) {
            return;
        }

        const value = this.modelPromptTextarea ? this.modelPromptTextarea.value : '';
        const savedPrompt = this.aiManager.setModelSystemPrompt(this.modelPromptModalModelRef, value);
        const label = this.modelPromptModalLabel || this.modelPromptModalModelRef?.name || '';

        if (savedPrompt && savedPrompt.length > 0) {
            this.showNotification(`Custom prompt saved for ${label}.`, 'success');
        } else {
            this.showNotification(`Removed custom prompt for ${label}.`, 'info');
        }

        this.updateModelSelectorUI();
        this.hideModelPromptModal();
    }

    handleModelPromptClear() {
        if (!this.aiManager || !this.modelPromptModalModelRef) {
            return;
        }

        if (this.modelPromptTextarea) {
            this.modelPromptTextarea.value = '';
            this.autosizeTextarea(this.modelPromptTextarea, 10);
        }

        this.aiManager.setModelSystemPrompt(this.modelPromptModalModelRef, '');
        const label = this.modelPromptModalLabel || this.modelPromptModalModelRef?.name || '';
        this.showNotification(`Removed custom prompt for ${label}.`, 'info');
        this.updateModelSelectorUI();
        this.hideModelPromptModal();
    }

    updateModelPromptPreview() {
        if (!this.aiManager) {
            return;
        }

        const globalPrompt = typeof this.aiManager.systemPrompt === 'string'
            ? this.aiManager.systemPrompt.trim()
            : '';
        const modelPrompt = this.modelPromptTextarea ? this.modelPromptTextarea.value.trim() : '';

        if (this.modelPromptGlobalPreviewEl) {
            if (globalPrompt) {
                this.modelPromptGlobalPreviewEl.textContent = globalPrompt;
                this.modelPromptGlobalPreviewEl.classList.remove('is-empty');
            } else {
                this.modelPromptGlobalPreviewEl.textContent = 'No global prompt configured.';
                this.modelPromptGlobalPreviewEl.classList.add('is-empty');
            }
        }

        if (this.modelPromptCombinedPreviewEl) {
            const combined = globalPrompt && modelPrompt
                ? `${globalPrompt}\n\n${modelPrompt}`
                : (modelPrompt || globalPrompt || '');

            if (combined) {
                this.modelPromptCombinedPreviewEl.textContent = combined;
                this.modelPromptCombinedPreviewEl.classList.remove('is-empty');
            } else {
                this.modelPromptCombinedPreviewEl.textContent = 'Model will only use the global prompt.';
                this.modelPromptCombinedPreviewEl.classList.add('is-empty');
            }
        }
    }

    updateModelSelectorUI() {
        if (!this.aiManager) return;

        const container = document.getElementById('modelCheckboxesContainer');
        const activeCountSpan = document.getElementById('activeModelCount');
        if (!container || !activeCountSpan) return;

        // --- Text Models ---
        const descriptors = this.aiManager.getTextModelDescriptors();
        const activeDescriptors = this.aiManager.getActiveTextModelDescriptors();

        // Update active count (image models UI hidden for now)
        const totalActiveModels = activeDescriptors.length;
        activeCountSpan.textContent = `${totalActiveModels} active`;
        container.innerHTML = '';
        
        // Create text models section header
        const textHeader = document.createElement('div');
        textHeader.className = 'model-section-header';
        textHeader.textContent = `📝 Text Models (${activeDescriptors.length})`;
        container.appendChild(textHeader);
        
        // Render Text Models
        if (descriptors.length > 0) {
            descriptors.forEach((descriptor) => {
                const checkboxItem = this.createModelCheckboxItem(descriptor, 'ai');
                container.appendChild(checkboxItem);
            });
        } else {
            const noTextModels = document.createElement('div');
            noTextModels.className = 'no-active-models';
            noTextModels.textContent = 'No text models.';
            container.appendChild(noTextModels);
        }
        
        // Image models section intentionally hidden

        this.renderChatModelToggles();
        this.updateChatModelInfo();
    }

    createModelCheckboxItem(descriptor, type = 'ai') {
        const isObjectDescriptor = descriptor && typeof descriptor === 'object';
        const reference = this.normalizeModelPromptReference(descriptor, isObjectDescriptor ? descriptor.label : null);
        const modelName = isObjectDescriptor && typeof descriptor.name === 'string'
            ? descriptor.name
            : typeof descriptor === 'string'
                ? descriptor
                : reference?.name;

        const checkboxKey = reference?.key || (isObjectDescriptor && typeof descriptor.index !== 'undefined'
            ? `${modelName || 'model'}__${descriptor.index}`
            : modelName || Math.random().toString(36).slice(2, 6));
        const checkboxId = `panel_${type}_${this.sanitizeDomId(checkboxKey, 'model')}`;

        const explicitActive = isObjectDescriptor && typeof descriptor.isActive === 'boolean'
            ? descriptor.isActive
            : null;
        const isActive = type === 'ai'
            ? (explicitActive !== null
                ? explicitActive
                : reference
                    ? this.aiManager?.isModelActive(reference) !== false
                    : true)
            : explicitActive === null
                ? true
                : explicitActive;

        const checkboxContainer = document.createElement('div');
        checkboxContainer.className = 'model-checkbox-item';

        const allowCustomPrompt = type === 'ai';
        const hasCustomPrompt = allowCustomPrompt && this.aiManager && typeof this.aiManager.hasModelSystemPrompt === 'function'
            ? this.aiManager.hasModelSystemPrompt(reference || modelName)
            : false;

        checkboxContainer.classList.add(isActive ? 'active' : 'inactive');
        if (hasCustomPrompt) {
            checkboxContainer.classList.add('has-custom-prompt');
        }

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = checkboxId;
        checkbox.checked = isActive;

        const label = document.createElement('label');
        label.htmlFor = checkboxId;
        label.classList.add('model-checkbox-label');

        const labelText = document.createElement('span');
        labelText.className = 'model-name';
        labelText.textContent = (reference && reference.label) || modelName || 'Model';
        label.appendChild(labelText);

        if (hasCustomPrompt) {
            const badge = document.createElement('span');
            badge.className = 'model-prompt-badge';
            badge.textContent = 'Custom';
            label.appendChild(badge);
        }

        let configBtn = null;
        if (allowCustomPrompt) {
            configBtn = document.createElement('button');
            configBtn.type = 'button';
            configBtn.className = 'model-config-btn';
            configBtn.textContent = '📝';
            configBtn.title = hasCustomPrompt
                ? `Edit custom prompt for ${(reference && reference.label) || modelName}`
                : `Add custom prompt for ${(reference && reference.label) || modelName}`;
            configBtn.setAttribute('aria-label', configBtn.title);
            if (hasCustomPrompt) {
                configBtn.classList.add('has-custom-prompt');
            }

            configBtn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.showModelPromptModal(reference || modelName);
            });
        }

        const updateStateClasses = (checked) => {
            const isChecked = Boolean(checked);
            checkboxContainer.classList.toggle('active', isChecked);
            checkboxContainer.classList.toggle('inactive', !isChecked);
        };

        checkbox.addEventListener('change', () => {
            if (type === 'ai') {
                this.aiManager.setModelActive(reference || modelName, checkbox.checked);
                this.updateModelSelectorUI();
            } else if (typeof modelName === 'string' && modelName) {
                this.aiManager.activeImageModels[modelName] = checkbox.checked;
                this.updateImageModelSelectorUI();
            }
            updateStateClasses(checkbox.checked);
        });

        checkboxContainer.appendChild(checkbox);
        checkboxContainer.appendChild(label);
        if (configBtn) {
            checkboxContainer.appendChild(configBtn);
        }

        updateStateClasses(checkbox.checked);

        return checkboxContainer;
    }

    updateImageModelSelectorUI() {
        // This function updates the image model selector UI
        // Since updateModelSelectorUI() already handles both text and image models,
        // we can simply call it to refresh the entire UI
        this.updateModelSelectorUI();
    }

    setupPanelDrag(panel, dragHandle, onDragEnd = null) {
        let isDragging = false;
        let dragOffset = { x: 0, y: 0 };
        let dragStart = { x: 0, y: 0 };
        let hasMoved = false;

        const onMouseDown = (e) => {
            if (e.button !== 0) return;
            if (!dragHandle.contains(e.target)) return;

            isDragging = true;
            hasMoved = false;
            dragStart.x = e.clientX;
            dragStart.y = e.clientY;

            const rect = panel.getBoundingClientRect();
            dragOffset.x = e.clientX - rect.left;
            dragOffset.y = e.clientY - rect.top;

            panel.classList.add('dragging');
            document.body.style.userSelect = 'none';

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;

            if (!hasMoved) {
                const deltaX = Math.abs(e.clientX - dragStart.x);
                const deltaY = Math.abs(e.clientY - dragStart.y);
                if (deltaX > 2 || deltaY > 2) {
                    hasMoved = true;
                }
            }

            let newX = e.clientX - dragOffset.x;
            let newY = e.clientY - dragOffset.y;

            const constraints = this.getPanelConstraints(panel);
            newX = Math.max(constraints.minX, Math.min(constraints.maxX, newX));
            newY = Math.max(constraints.minY, Math.min(constraints.maxY, newY));

            panel.style.left = newX + 'px';
            panel.style.top = newY + 'px';
            panel.style.right = 'auto';
            panel.style.bottom = 'auto';
        };

        const onMouseUp = () => {
            if (!isDragging) return;

            isDragging = false;
            panel.classList.remove('dragging');
            document.body.style.userSelect = '';

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            this.savePanelPosition(panel);

            if (typeof onDragEnd === 'function') {
                onDragEnd(hasMoved);
            }
        };

        dragHandle.addEventListener('mousedown', onMouseDown);
    }

    getPanelConstraints(panel) {
        const panelRect = panel.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 10;

        return {
            minX: margin,
            minY: margin,
            maxX: viewportWidth - panelRect.width - margin,
            maxY: viewportHeight - panelRect.height - margin
        };
    }

    loadPanelPosition(panel) {
        const savedPosition = localStorage.getItem('model_panel_position');
        if (savedPosition) {
            try {
                const position = JSON.parse(savedPosition);
                const constraints = this.getPanelConstraints(panel);
                const validX = Math.max(constraints.minX, Math.min(constraints.maxX, position.x));
                const validY = Math.max(constraints.minY, Math.min(constraints.maxY, position.y));

                panel.style.left = validX + 'px';
                panel.style.top = validY + 'px';
                panel.style.right = 'auto';
                panel.style.bottom = 'auto';
            } catch (e) {
                console.warn('Failed to load panel position:', e);
            }
        }
    }

    savePanelPosition(panel) {
        const rect = panel.getBoundingClientRect();
        const position = {
            x: rect.left,
            y: rect.top
        };
        localStorage.setItem('model_panel_position', JSON.stringify(position));
    }

    constrainPanelToBounds(panel) {
        const rect = panel.getBoundingClientRect();
        const constraints = this.getPanelConstraints(panel);

        let newX = rect.left;
        let newY = rect.top;
        let needsUpdate = false;

        if (newX < constraints.minX) {
            newX = constraints.minX;
            needsUpdate = true;
        } else if (newX > constraints.maxX) {
            newX = constraints.maxX;
            needsUpdate = true;
        }

        if (newY < constraints.minY) {
            newY = constraints.minY;
            needsUpdate = true;
        } else if (newY > constraints.maxY) {
            newY = constraints.maxY;
            needsUpdate = true;
        }

        if (needsUpdate) {
            panel.style.left = newX + 'px';
            panel.style.top = newY + 'px';
            this.savePanelPosition(panel);
        }
    }

    // Content Modal
    setupContentModal() {
        // Modal close handlers
        document.getElementById('closeModalBtn').addEventListener('click', () => this.hideContentModal());
        document.getElementById('contentModal').addEventListener('click', (e) => {
            if (e.target.classList.contains('modal-backdrop')) {
                this.hideContentModal();
            }
        });
        document.getElementById('editModalBtn').addEventListener('click', () => this.toggleModalEditMode());
    }

    showContentModal() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.showNotification('Please select exactly one node to expand its content', 'warning');
            return;
        }

        const selectedNode = this.canvasState.selectedNodes[0];
        const modal = document.getElementById('contentModal');
        const modalTitle = document.getElementById('modalTitle');
        const modalContent = document.getElementById('modalContent');

        modalTitle.textContent = 'Node Content';

        const htmlContent = this.markdownToHtml(
            selectedNode.text || 'No content',
            selectedNode.fontSize || this.canvasState.globalFontSize
        );
        modalContent.innerHTML = htmlContent;

        modal.classList.remove('hidden');

        this.modalEscapeHandler = (e) => {
            if (e.key === 'Escape') {
                this.hideContentModal();
            }
        };
        document.addEventListener('keydown', this.modalEscapeHandler);
    }

    hideContentModal() {
        const modal = document.getElementById('contentModal');
        modal.classList.add('hidden');

        if (this.modalEscapeHandler) {
            document.removeEventListener('keydown', this.modalEscapeHandler);
            this.modalEscapeHandler = null;
        }

        this.resetModalToViewMode();
    }

    toggleModalEditMode() {
        const modalContent = document.getElementById('modalContent');
        
        if (modalContent.querySelector('.modal-edit-textarea')) {
            this.resetModalToViewMode();
        } else {
            this.enterModalEditMode();
        }
    }

    enterModalEditMode() {
        if (this.canvasState.selectedNodes.length !== 1) return;

        const selectedNode = this.canvasState.selectedNodes[0];
        const modalContent = document.getElementById('modalContent');
        const editBtn = document.getElementById('editModalBtn');

        const textarea = document.createElement('textarea');
        textarea.className = 'modal-edit-textarea';
        textarea.value = selectedNode.text || '';
        
        const controlsDiv = document.createElement('div');
        controlsDiv.className = 'modal-edit-controls';
        
        const saveBtn = document.createElement('button');
        saveBtn.className = 'modal-save-btn';
        saveBtn.textContent = 'Save Changes';
        saveBtn.addEventListener('click', () => this.saveModalEdit());
        
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'modal-cancel-btn';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => this.resetModalToViewMode());
        
        controlsDiv.appendChild(cancelBtn);
        controlsDiv.appendChild(saveBtn);
        
        modalContent.innerHTML = '';
        modalContent.appendChild(textarea);
        modalContent.appendChild(controlsDiv);
        
        editBtn.textContent = '💾';
        editBtn.title = 'Save & Exit Edit Mode';
        
        textarea.focus();
        
        textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.resetModalToViewMode();
            } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this.saveModalEdit();
            }
        });
    }

    saveModalEdit() {
        const selectedNode = this.canvasState.selectedNodes[0];
        const textarea = document.querySelector('.modal-edit-textarea');
        
        if (selectedNode && textarea) {
            this.canvasState.saveState();
            selectedNode.text = textarea.value;
            this.canvasState.saveToLocalStorage();
            this.resetModalToViewMode();
        }
    }

    resetModalToViewMode() {
        if (this.canvasState.selectedNodes.length !== 1) return;

        const selectedNode = this.canvasState.selectedNodes[0];
        const modalContent = document.getElementById('modalContent');
        const editBtn = document.getElementById('editModalBtn');

        const htmlContent = this.markdownToHtml(
            selectedNode.text || 'No content',
            selectedNode.fontSize || this.canvasState.globalFontSize
        );
        modalContent.innerHTML = htmlContent;

        editBtn.textContent = '✏️';
        editBtn.title = 'Edit Content';
    }

    markdownToHtml(text, baseFontSize = 14) {
        if (!text || typeof text !== 'string') {
            return '<p>No content</p>';
        }

        const parsed = parseMarkdown(text, baseFontSize);
        let html = '';

        parsed.forEach(item => {
            if (item.type === 'spacing') {
                html += '<br>';
                return;
            }

            if (item.type === 'header') {
                const level = item.level || 1;
                html += `<h${level}>${this.escapeHtml(item.content)}</h${level}>`;
            } else if (item.type === 'list') {
                const content = Array.isArray(item.content)
                    ? this.processInlineFormattingToHtml(item.content)
                    : this.escapeHtml(item.content);
                html += `<li>${content}</li>`;
            } else {
                const content = Array.isArray(item.content)
                    ? this.processInlineFormattingToHtml(item.content)
                    : this.escapeHtml(item.content);
                html += `<p>${content}</p>`;
            }
        });

        html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');
        return html || '<p>No content</p>';
    }

    processInlineFormattingToHtml(segments) {
        return segments.map(segment => {
            const text = this.escapeHtml(segment.text);
            switch (segment.type) {
                case 'bold':
                    return `<strong>${text}</strong>`;
                case 'italic':
                    return `<em>${text}</em>`;
                case 'code':
                    return `<code>${text}</code>`;
                default:
                    return text;
            }
        }).join('');
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Tooltip and status updates
    updateGenerateIdeasTooltip() {
        if (!this.aiManager) return;
        
        const tooltip = document.getElementById('generateIdeasTooltip');
        const generateBtn = document.getElementById('generateIdeasBtn');
        const enterChatBtn = document.getElementById('enterChatBtn');
        const generateImageBtn = document.getElementById('generateImageBtn');
        const generateReferenceBtn = document.getElementById('generateReferenceBtn');
        const uploadImageBtn = document.getElementById('uploadImageBtn');
        const contextImageBtn = document.getElementById('contextImageBtn');
        const generateVideoBtn = document.getElementById('generateVideoBtn');
        const downloadImageBtn = document.getElementById('downloadImageBtn');

        if (this.canvasState.selectedNodes.length === 1) {
            const selectedNode = this.canvasState.selectedNodes[0];
            
            // Get canvas element to calculate screen position
            const canvas = document.getElementById('canvas');
            if (!canvas || !tooltip) return;
            
            const rect = canvas.getBoundingClientRect();

            // Position tooltip above the selected node
            const nodeScreenX = rect.left + (selectedNode.x * this.canvasState.scale) + this.canvasState.offsetX;
            const nodeScreenY = rect.top + (selectedNode.y * this.canvasState.scale) + this.canvasState.offsetY;
            const tooltipX = nodeScreenX + (selectedNode.width * this.canvasState.scale) / 2;
            const tooltipY = nodeScreenY - 60; // 60px above the node to accommodate buttons

            tooltip.style.left = `${tooltipX}px`;
            tooltip.style.top = `${tooltipY}px`;
            tooltip.style.transform = 'translateX(-50%)'; // Center horizontally
            tooltip.classList.remove('hidden');

            // Update generate button state
            if (generateBtn) {
                if (this.aiManager.hasAnyTextProvider() || this.aiManager.automanaged) {
                    generateBtn.style.display = 'flex';
                    generateBtn.disabled = selectedNode.isGeneratingAI;
                    generateBtn.textContent = selectedNode.isGeneratingAI ? '⏳' : '✨';
                    generateBtn.title = selectedNode.isGeneratingAI ? 'Generating...' : 'Generate Ideas';
                } else {
                    generateBtn.style.display = 'none';
                }
            }

            if (enterChatBtn) {
                enterChatBtn.style.display = 'flex';
                enterChatBtn.disabled = false;
                enterChatBtn.textContent = '💬';
                enterChatBtn.title = 'Enter Chat Mode for this branch';
            }

            // Update generate image button
            if (generateImageBtn) {
                if (this.aiManager.replicateApiKey) {
                    generateImageBtn.style.display = 'flex';
                    generateImageBtn.disabled = selectedNode.isGeneratingImage;
                    generateImageBtn.textContent = selectedNode.isGeneratingImage ? '⏳' : '🖼️';
                    generateImageBtn.title = selectedNode.isGeneratingImage ? 'Generating...' : 'Generate Image';
                } else {
                    generateImageBtn.style.display = 'none';
                }
            }

            // Update generate with reference button
            if (generateReferenceBtn) {
                const hasConnectedImages = this.hasConnectedImageNode(selectedNode);
                
                if (this.aiManager.replicateApiKey && hasConnectedImages) {
                    generateReferenceBtn.style.display = 'flex';
                    generateReferenceBtn.disabled = selectedNode.isGeneratingReference;
                    generateReferenceBtn.textContent = selectedNode.isGeneratingReference ? '⏳' : '🎯';
                    generateReferenceBtn.title = selectedNode.isGeneratingReference ? 'Generating...' : 'Generate with Reference (Gen-4)';
                } else {
                    generateReferenceBtn.style.display = 'none';
                }
            }

            // Always show upload image button for single node selection
            if (uploadImageBtn) {
                uploadImageBtn.style.display = 'flex';
                uploadImageBtn.disabled = selectedNode.isUploadingImage;
                uploadImageBtn.textContent = selectedNode.isUploadingImage ? '\u23f3' : '\ud83d\udcc1';
                uploadImageBtn.title = selectedNode.isUploadingImage ? 'Uploading...' : 'Upload Image';
            }

            // Show download image button if node has an image
            if (downloadImageBtn) {
                if (selectedNode.generatedImageUrl) {
                    downloadImageBtn.style.display = 'flex';
                    downloadImageBtn.disabled = false;
                    downloadImageBtn.title = 'Download Image';
                } else {
                    downloadImageBtn.style.display = 'none';
                }
            }

            // Show context image button if replicate API key is available and node has text content
            if (contextImageBtn) {
                // Check if this text node has a connected image node
                const hasConnectedImage = this.hasConnectedImageNode(selectedNode);
                
                if (this.aiManager.replicateApiKey && selectedNode.text && selectedNode.text.trim() !== '' && selectedNode.text.trim() !== 'New Node') {
                    contextImageBtn.style.display = 'flex';
                    contextImageBtn.disabled = selectedNode.isGeneratingContext;
                    contextImageBtn.textContent = selectedNode.isGeneratingContext ? '⏳' : '🎨';
                    if (hasConnectedImage) {
                        contextImageBtn.title = selectedNode.isGeneratingContext ? 'Editing...' : 'Edit Connected Image with Context';
                    } else {
                        contextImageBtn.title = selectedNode.isGeneratingContext ? 'Generating...' : 'Generate & Edit Image';
                    }
                } else {
                    contextImageBtn.style.display = 'none';
                }
            }

            // Show video generation button if replicate API key is available and node has text content
            if (generateVideoBtn) {
                if (this.aiManager.replicateApiKey && selectedNode.text && selectedNode.text.trim() !== '' && selectedNode.text.trim() !== 'New Node') {
                    generateVideoBtn.style.display = 'flex';
                    generateVideoBtn.disabled = selectedNode.isGeneratingVideo;
                    generateVideoBtn.textContent = selectedNode.isGeneratingVideo ? '⏳' : '🎬';
                    
                    // Check if there's a connected image to determine the mode
                    const hasConnectedImage = this.hasConnectedImageNode(selectedNode);
                    if (hasConnectedImage) {
                        generateVideoBtn.title = selectedNode.isGeneratingVideo ? 'Generating...' : 'Generate Video from Connected Image';
                    } else {
                        generateVideoBtn.title = selectedNode.isGeneratingVideo ? 'Generating...' : 'Generate Video from Text';
                    }
                } else {
                    generateVideoBtn.style.display = 'none';
                }
            }

        } else {
            if (tooltip) tooltip.classList.add('hidden');
            if (generateBtn) {
                generateBtn.disabled = true;
            }
            if (enterChatBtn) {
                enterChatBtn.disabled = true;
                enterChatBtn.style.display = 'none';
            }
            if (generateImageBtn) {
                generateImageBtn.disabled = true;
            }
            if (generateReferenceBtn) {
                generateReferenceBtn.disabled = true;
            }
            if (uploadImageBtn) {
                uploadImageBtn.disabled = true;
            }
            if (contextImageBtn) {
                contextImageBtn.disabled = true;
            }
            if (generateVideoBtn) {
                generateVideoBtn.disabled = true;
            }
            if (downloadImageBtn) {
                downloadImageBtn.disabled = true;
            }
        }
    }

    updateSelectionStatus() {
        const statusElement = document.getElementById('selectionStatus');
        const countElement = document.getElementById('selectionCount');
        const hintElement = document.getElementById('selectionHint');

        if (this.canvasState.selectedNodes.length > 0) {
            statusElement.classList.remove('hidden');
            countElement.textContent = this.canvasState.selectedNodes.length;

            if (this.canvasState.selectedNodes.length > 1) {
                hintElement.classList.remove('hidden');
                hintElement.textContent = 'Select a single node to generate AI ideas';
            } else {
                hintElement.classList.add('hidden');
            }
        } else {
            statusElement.classList.add('hidden');
        }
    }

    // Notifications
    showNotification(message, type = 'info', duration = 3000) {
        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, duration);
    }

    showProgressNotification(message, models = []) {
        this.hideProgressNotification();

        const progressNotification = document.createElement('div');
        progressNotification.id = 'aiProgressNotification';
        progressNotification.className = 'progress-notification';

        const normalizedModels = Array.isArray(models)
            ? models.map((entry, index) => {
                  if (typeof entry === 'string') {
                      const name = entry.trim();
                      const label = name.split('/').pop() || name || `Model ${index + 1}`;
                      return {
                          key: `${name || 'model'}__${index}`,
                          name,
                          label
                      };
                  }

                  if (entry && typeof entry === 'object') {
                      const name = typeof entry.name === 'string' ? entry.name : (typeof entry.model === 'string' ? entry.model : '');
                      const fallbackLabel = name.split('/').pop() || name || `Model ${index + 1}`;
                      const key = typeof entry.key === 'string' && entry.key.length > 0
                          ? entry.key
                          : `${name || 'model'}__${index}`;
                      const label = typeof entry.label === 'string' && entry.label.trim().length > 0
                          ? entry.label.trim()
                          : fallbackLabel;
                      return {
                          key,
                          name,
                          label
                      };
                  }

                  return {
                      key: `model__${index}`,
                      name: '',
                      label: `Model ${index + 1}`
                  };
              })
            : [];

        progressNotification.innerHTML = `
            <div class="progress-header">
                <span class="progress-message">${message}</span>
                <button class="progress-close" onclick="this.parentElement.parentElement.remove()">×</button>
            </div>
            <div class="progress-models">
                ${normalizedModels.map(model => {
                    const safeKey = (model.key || '').replace(/"/g, '&quot;');
                    const safeName = (model.name || '').replace(/"/g, '&quot;');
                    return `<div class="model-status" data-model="${safeName}" data-model-key="${safeKey}">
                        <div class="model-row">
                            <span class="model-name">${model.label}</span>
                            <span class="model-indicator" aria-label="pending">⏳</span>
                        </div>
                        <div class="model-preview hidden" data-preview></div>
                    </div>`;
                }).join('')}
            </div>
            <div class="progress-summary">
                <span id="progressSummary">Preparing...</span>
            </div>
        `;

        document.body.appendChild(progressNotification);
    }

    updateProgressNotification(completed, total, totalNodes) {
        const progressNotification = document.getElementById('aiProgressNotification');
        if (!progressNotification) return;

        const summaryElement = document.getElementById('progressSummary');
        if (summaryElement) {
            summaryElement.textContent = `${completed}/${total} models completed • ${totalNodes} ideas generated`;
        }
    }

    updateProgressModelStatus(modelIdentifier, { status = 'pending', message = '', ideaCount = 0 } = {}) {
        const progressNotification = document.getElementById('aiProgressNotification');
        if (!progressNotification) {
            return;
        }

        const identifier = modelIdentifier !== undefined && modelIdentifier !== null
            ? String(modelIdentifier)
            : null;

        const modelElements = progressNotification.querySelectorAll('.model-status');
        const target = Array.from(modelElements).find((el) => {
            if (!identifier) return false;
            if (el.dataset.modelKey && el.dataset.modelKey === identifier) {
                return true;
            }
            return el.dataset.model === identifier;
        });
        if (!target) {
            return;
        }

        const indicator = target.querySelector('.model-indicator');
        if (indicator) {
            let symbol = '⏳';
            if (status === 'success') {
                symbol = '✅';
            } else if (status === 'error') {
                symbol = '❌';
            }
            indicator.textContent = symbol;
            indicator.setAttribute('aria-label', status);
        }

        const sanitizedMessage = typeof message === 'string' ? message.trim() : '';
        const preview = target.querySelector('[data-preview]');
        if (preview) {
            preview.textContent = sanitizedMessage;
            if (sanitizedMessage.length > 0) {
                preview.classList.remove('hidden');
            } else {
                preview.classList.add('hidden');
            }
        }

        if (typeof ideaCount === 'number' && ideaCount >= 0) {
            target.setAttribute('data-ideas', ideaCount.toString());
        }
    }

    hideProgressNotification() {
        const progressNotification = document.getElementById('aiProgressNotification');
        if (progressNotification && progressNotification.parentNode) {
            progressNotification.parentNode.removeChild(progressNotification);
        }
    }

    // Export/Import functionality
    exportJSON() {
        try {
            const activeThreadId = this.canvasState.activeThreadId ? this.canvasState.activeThreadId.toString() : null;
            let nodesToExport = [];
            if (activeThreadId) {
                nodesToExport = this.getNodesForThread(activeThreadId).filter(node => node && node.id);
            }
            if (!Array.isArray(nodesToExport) || nodesToExport.length === 0) {
                nodesToExport = (this.canvasState.nodes || []).filter(node => node && node.id);
            }
            const nodeIdSet = new Set(
                nodesToExport
                    .map(node => (node && node.id ? node.id.toString() : null))
                    .filter(Boolean)
            );

            // Export in Obsidian Canvas format
            const obsidianData = {
                nodes: nodesToExport
                    .map(node => ({
                        id: node.id.toString(),
                        type: node.type || "text",
                        text: node.text || '',
                        x: Math.round(node.x || 0),
                        y: Math.round(node.y || 0),
                        width: Math.round(node.width || 360),
                        height: Math.round(node.height || 140)
                    })),
                edges: (this.canvasState.connections || [])
                    .filter(conn => {
                        // Filter out invalid connections
                        const fromNodeId = conn.fromNode || conn.from;
                        const toNodeId = conn.toNode || conn.to;
                        if (!fromNodeId || !toNodeId || !conn.id) {
                            return false;
                        }
                        const normalizedFrom = fromNodeId.toString();
                        const normalizedTo = toNodeId.toString();
                        return nodeIdSet.has(normalizedFrom) && nodeIdSet.has(normalizedTo);
                    })
                    .map(conn => ({
                        id: conn.id.toString(),
                        fromNode: (conn.fromNode || conn.from).toString(),
                        toNode: (conn.toNode || conn.to).toString(),
                        ...(conn.fromSide && { fromSide: conn.fromSide }),
                        ...(conn.toSide && { toSide: conn.toSide })
                    }))
            };

            const jsonString = JSON.stringify(obsidianData, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'canvas-export.canvas';
            a.style.display = 'none';

            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                if (a.parentNode) {
                    document.body.removeChild(a);
                }
                URL.revokeObjectURL(url);
            }, 100);

            this.showNotification('Obsidian Canvas exported successfully!', 'success');

        } catch (error) {
            console.error('Error during export:', error);
            this.showNotification('Export failed: ' + error.message, 'error');
        }
    }

    importJSON() {
        document.getElementById('importFileInput').click();
    }
    
    updateUndoRedoButtons() {
        const undoBtn = document.getElementById('undoBtn');
        const redoBtn = document.getElementById('redoBtn');

        if (undoBtn) {
            undoBtn.disabled = !this.canvasState.canUndo();
        }
        if (redoBtn) {
            redoBtn.disabled = !this.canvasState.canRedo();
        }
    }

    async downloadNodeImage(node) {
        if (!node || !node.generatedImageUrl) return;
        
        // Download the image from node.generatedImageUrl as a blob for direct download
        try {
            const response = await fetch(node.generatedImageUrl, {mode: 'cors'});
            if (!response.ok) throw new Error('Failed to fetch image');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'node-image.png';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                if (a.parentNode) {
                    document.body.removeChild(a);
                }
                URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            this.showNotification('Failed to download image: ' + err.message, 'error');
        }
    }

    hasConnectedImageNode(node) {
        // Check if this text node has any connected image nodes
        const connections = this.canvasState.connections;
        const connectedNodeIds = new Set();
        
        // Find all nodes connected to this node (both incoming and outgoing connections)
        connections.forEach(conn => {
            if (conn.from === node.id) {
                connectedNodeIds.add(conn.to);
            }
            if (conn.to === node.id) {
                connectedNodeIds.add(conn.from);
            }
        });
        
        // Check if any connected nodes are image nodes (media reference nodes with images)
        const imageNodes = this.canvasState.nodes.filter(connectedNode => 
            connectedNodeIds.has(connectedNode.id) && 
            connectedNode.isMediaReference && 
            connectedNode.generatedImageUrl
        );
        
        return imageNodes.length > 0;
    }

    // Aspect Ratio Selector
    setupAspectRatioSelector() {
        const aspectRatioSelector = document.getElementById('aspectRatioSelector');
        const generateImageBtn = document.getElementById('generateImageBtn');
        const generateReferenceBtn = document.getElementById('generateReferenceBtn');
        
        if (!aspectRatioSelector || !generateImageBtn || !generateReferenceBtn) return;

        // Setup aspect ratio option buttons
        const aspectRatioOptions = aspectRatioSelector.querySelectorAll('.aspect-ratio-option');
        aspectRatioOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Update active state
                aspectRatioOptions.forEach(opt => opt.classList.remove('active'));
                option.classList.add('active');
                
                // Update selected aspect ratio
                this.selectedAspectRatio = option.dataset.ratio;
                
                // Hide selector after selection
                this.hideAspectRatioSelector();
            });
        });

        // Setup hover handlers for image generation buttons
        [generateImageBtn, generateReferenceBtn].forEach(btn => {
            btn.addEventListener('mouseenter', (e) => {
                if (!btn.disabled) {
                    this.showAspectRatioSelector(btn);
                }
            });

            btn.addEventListener('mouseleave', (e) => {
                // Check if mouse is moving towards the selector
                const rect = btn.getBoundingClientRect();
                const selectorRect = aspectRatioSelector.getBoundingClientRect();
                
                // Only hide if not moving towards selector area
                this.aspectRatioTimeout = setTimeout(() => {
                    if (!this.aspectRatioSelectorVisible) return;
                    
                    // Double-check mouse position
                    const mouseX = e.clientX;
                    const mouseY = e.clientY;
                    
                    // If mouse is within selector area, don't hide
                    if (mouseX >= selectorRect.left && mouseX <= selectorRect.right &&
                        mouseY >= selectorRect.top && mouseY <= selectorRect.bottom) {
                        return;
                    }
                    
                    this.hideAspectRatioSelector();
                }, 500); // Increased delay to 500ms
            });
        });

        // Keep selector visible when hovering over it
        aspectRatioSelector.addEventListener('mouseenter', () => {
            // Clear any pending hide timeout
            if (this.aspectRatioTimeout) {
                clearTimeout(this.aspectRatioTimeout);
                this.aspectRatioTimeout = null;
            }
        });

        aspectRatioSelector.addEventListener('mouseleave', (e) => {
            // Only hide if mouse is not moving back to buttons
            const generateImageRect = generateImageBtn.getBoundingClientRect();
            const generateReferenceRect = generateReferenceBtn.getBoundingClientRect();
            
            this.aspectRatioTimeout = setTimeout(() => {
                const mouseX = e.clientX;
                const mouseY = e.clientY;
                
                // Check if mouse is over either button
                const overImageBtn = mouseX >= generateImageRect.left && mouseX <= generateImageRect.right &&
                                   mouseY >= generateImageRect.top && mouseY <= generateImageRect.bottom;
                const overReferenceBtn = mouseX >= generateReferenceRect.left && mouseX <= generateReferenceRect.right &&
                                        mouseY >= generateReferenceRect.top && mouseY <= generateReferenceRect.bottom;
                
                if (!overImageBtn && !overReferenceBtn) {
                    this.hideAspectRatioSelector();
                }
            }, 200); // Shorter delay when leaving selector
        });
    }

    setViewMode(mode, { skipStorage = false } = {}) {
        const normalizedMode = mode === 'chat' ? 'chat' : 'canvas';

        if (normalizedMode === 'canvas') {
            const threadRoot = this.ensureActiveCanvasThread({ createIfMissing: true });
            if (!threadRoot) {
                this.showNotification('Unable to open canvas mode. Start a chat to create a canvas.', 'warning');
                return;
            }
        }

        const prevMode = this.currentViewMode;

        if (prevMode === 'chat' && normalizedMode !== 'chat') {
            this.saveCurrentChatScrollPosition();
        }

        if (prevMode === normalizedMode) {
            this.updateChatVisibility(this.currentViewMode);
            if (this.currentViewMode === 'chat') {
                this.updateChatModelInfo();
                this.applyChatSidebarState();
            } else {
                this.ensureCanvasVisible();
            }
            if (this.currentViewMode === 'canvas') {
                this.scheduleFocusActiveCanvasThread();
            }
            return;
        }

        this.currentViewMode = normalizedMode;

        if (!skipStorage) {
            try {
                localStorage.setItem('ui_view_mode', this.currentViewMode);
            } catch (storageError) {
                console.warn('Unable to persist view mode preference:', storageError);
            }
        }

        this.updateChatVisibility(this.currentViewMode);

        if (this.currentViewMode === 'chat') {
            this.updateChatModelInfo();
            this.applyChatSidebarState();
            setTimeout(() => {
                const chatInput = document.getElementById('chatInput');
                if (chatInput) {
                    chatInput.focus();
                }
                this.restoreChatScrollPosition({ fallbackToBottom: true });
            }, 50);
        } else {
            this.ensureCanvasVisible();
        }
        if (this.currentViewMode === 'canvas') {
            this.scheduleFocusActiveCanvasThread();
        }
    }

    updateChatVisibility(mode) {
        const chatView = document.getElementById('chatView');
        if (!chatView) return;

        if (mode === 'chat') {
            document.body.classList.add('chat-mode');
            chatView.classList.remove('hidden');
            if (this.returnToChatBtn) {
                this.returnToChatBtn.classList.add('hidden');
            }
        } else {
            document.body.classList.remove('chat-mode');
            chatView.classList.add('hidden');
            this.hideChatModelModal();
            if (this.returnToChatBtn) {
                this.returnToChatBtn.classList.remove('hidden');
            }
        }
    }

    ensureCanvasVisible() {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            return;
        }

        window.requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });
    }

    scheduleFocusActiveCanvasThread() {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            this.focusActiveCanvasThread();
            return;
        }

        if (this.pendingCanvasFocusFrame && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(this.pendingCanvasFocusFrame);
        }

        this.pendingCanvasFocusFrame = window.requestAnimationFrame(() => {
            this.pendingCanvasFocusFrame = null;
            this.focusActiveCanvasThread();
        });
    }

    focusActiveCanvasThread({ padding = 120 } = {}) {
        if (!this.canvasState || !this.canvasElement) {
            return;
        }

        const activeThreadId = this.canvasState.activeThreadId ? this.canvasState.activeThreadId.toString() : null;
        if (!activeThreadId) {
            return;
        }

        const threadNodes = this.getNodesForThread(activeThreadId);
        if (!Array.isArray(threadNodes) || threadNodes.length === 0) {
            return;
        }

        const bounds = threadNodes.reduce((acc, node) => {
            const nodeX = typeof node.x === 'number' ? node.x : 0;
            const nodeY = typeof node.y === 'number' ? node.y : 0;
            const nodeWidth = typeof node.width === 'number' ? node.width : 0;
            const nodeHeight = typeof node.height === 'number' ? node.height : 0;

            acc.minX = Math.min(acc.minX, nodeX);
            acc.minY = Math.min(acc.minY, nodeY);
            acc.maxX = Math.max(acc.maxX, nodeX + nodeWidth);
            acc.maxY = Math.max(acc.maxY, nodeY + nodeHeight);
            return acc;
        }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

        if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.minY) ||
            !Number.isFinite(bounds.maxX) || !Number.isFinite(bounds.maxY)) {
            return;
        }

        const rect = typeof this.canvasElement.getBoundingClientRect === 'function'
            ? this.canvasElement.getBoundingClientRect()
            : null;
        const canvasWidth = this.canvasElement.width || (rect ? rect.width : 0);
        const canvasHeight = this.canvasElement.height || (rect ? rect.height : 0);

        if (!(canvasWidth > 0) || !(canvasHeight > 0)) {
            return;
        }

        const currentScale = typeof this.canvasState.scale === 'number' && this.canvasState.scale > 0
            ? this.canvasState.scale
            : 1;
        const safePadding = Math.max(24, typeof padding === 'number' ? padding : 120);
        const width = Math.max(1, bounds.maxX - bounds.minX);
        const height = Math.max(1, bounds.maxY - bounds.minY);

        const centerX = bounds.minX + width / 2;
        const centerY = bounds.minY + height / 2;

        this.canvasState.offsetX = canvasWidth / 2 - centerX * currentScale;
        this.canvasState.offsetY = canvasHeight / 2 - centerY * currentScale;

        const availableWidth = canvasWidth - safePadding * 2;
        const availableHeight = canvasHeight - safePadding * 2;
        if (availableWidth > 0 && availableHeight > 0) {
            const requiredScaleX = availableWidth / width;
            const requiredScaleY = availableHeight / height;
            const fitScale = Math.min(requiredScaleX, requiredScaleY);

            if (fitScale < currentScale) {
                const clampedScale = Math.max(0.2, fitScale);
                this.canvasState.scale = clampedScale;
                this.canvasState.offsetX = canvasWidth / 2 - centerX * clampedScale;
                this.canvasState.offsetY = canvasHeight / 2 - centerY * clampedScale;
            }
        }
    }

    setupChatUI() {
        if (this.chatUIInitialized) return;

        const chatForm = document.getElementById('chatForm');
        const chatInput = document.getElementById('chatInput');
        const chatMessages = document.getElementById('chatMessages');
        const chatSendBtn = document.getElementById('chatSendBtn');
        const newChatBtn = document.getElementById('newChatBtn');
        const chatConfigBtn = document.getElementById('chatConfigBtn');
        const chatModelStackBtn = document.getElementById('chatModelStackBtn');
        const chatCanvasModeBtn = document.getElementById('chatCanvasModeBtn');

        if (!chatForm || !chatInput || !chatMessages || !chatSendBtn || !newChatBtn) return;

        this.chatCanvasModeBtn = chatCanvasModeBtn;
        this.chatSidebarEl = document.getElementById('chatSidebar');
        this.chatTreeContainer = document.getElementById('chatTree');
        this.chatSidebarCollapseBtn = document.getElementById('chatSidebarCollapseBtn');
        this.chatSidebarShowBtn = document.getElementById('chatSidebarShowBtn');
        this.chatContentEl = document.querySelector('#chatView .chat-content');

        if (this.chatTreeContainer && !this.chatTreeScrollHandlerAttached) {
            this.chatTreeContainer.addEventListener('scroll', () => {
                this.chatTreeScrollTop = this.chatTreeContainer.scrollTop;
            });
            this.chatTreeScrollHandlerAttached = true;
        }

        if (this.chatSidebarCollapseBtn) {
            this.chatSidebarCollapseBtn.addEventListener('click', () => {
                this.setChatSidebarCollapsed(!this.chatSidebarCollapsed);
            });
        }

        if (this.chatSidebarShowBtn) {
            this.chatSidebarShowBtn.addEventListener('click', () => {
                this.setChatSidebarCollapsed(false);
                if (this.chatSidebarCollapseBtn && typeof this.chatSidebarCollapseBtn.focus === 'function') {
                    try {
                        this.chatSidebarCollapseBtn.focus({ preventScroll: true });
                    } catch (focusError) {
                        this.chatSidebarCollapseBtn.focus();
                    }
                }
            });
        }

        this.applyChatSidebarState();

        this.chatUIInitialized = true;
        this.chatMessagesContainer = chatMessages;
        this.chatAutoScroll = true;
        if (!this.boundChatScrollHandler) {
            this.boundChatScrollHandler = () => this.handleChatScroll();
        }
        this.chatMessagesContainer.addEventListener('scroll', this.boundChatScrollHandler);
        this.chatInputEl = chatInput;
        this.chatSendBtnEl = chatSendBtn;
        this.chatSendBtnDefaultLabel = chatSendBtn.textContent || 'Send';
        this.chatBranchNavEl = document.getElementById('chatBranchNav');
        this.chatModelToggleEl = document.getElementById('chatModelToggles');
        this.chatModelInfoEl = document.getElementById('chatModelInfo');
        this.chatModelModalEl = document.getElementById('chatModelModal');

        this.autosizeTextarea(chatInput, 7);
        chatInput.addEventListener('input', () => {
            this.autosizeTextarea(chatInput, 7);
        });

        const processSubmit = async () => {
            if (this.isChatLoading) {
                return;
            }

            const message = chatInput.value.trim();
            if (!message) {
                return;
            }

            if (!this.aiManager) {
                this.showSystemMessage('AI services are still initializing. Please try again in a moment.');
                return;
            }

            this.appendChatConversationMessage('user', message);
            chatInput.value = '';
            this.autosizeTextarea(chatInput, 7);

            try {
                await this.requestChatCompletion();
            } catch (error) {
                console.error('❌ Chat submission error:', error);
            }
        };

        chatForm.addEventListener('submit', (event) => {
            event.preventDefault();
            processSubmit();
        });

        chatInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
                event.preventDefault();
                processSubmit();
            }
        });

        newChatBtn.addEventListener('click', () => {
            this.startNewChat();
        });

        // Add event listener for the new chat toolbar gear button
        const chatConfigToolbarBtn = document.getElementById('chatConfigToolbarBtn');
        if (chatConfigToolbarBtn) {
            chatConfigToolbarBtn.addEventListener('click', () => {
                this.showApiKeyModal();
            });
        }

        if (chatModelStackBtn && this.chatModelModalEl) {
            chatModelStackBtn.addEventListener('click', () => this.showChatModelModal());
        }

        if (chatCanvasModeBtn) {
            chatCanvasModeBtn.addEventListener('click', () => {
                const rootNode = this.ensureActiveCanvasThread({ createIfMissing: true });
                if (!rootNode || !rootNode.id) {
                    this.showNotification('Unable to open canvas mode. Start a chat to create a canvas.', 'warning');
                    return;
                }

                const threadId = rootNode.id.toString();
                this.canvasState.setActiveThread(threadId);
                this.activeCanvasThreadId = threadId;

                if (!Array.isArray(this.chatBranchNodes) || this.chatBranchNodes.length === 0) {
                    this.chatBranchNodes = [rootNode];
                    this.chatFocusNodeId = rootNode.id;
                    this.lastChatNode = rootNode;
                    this.updateBranchState({ preserveBase: false });
                }

                this.setViewMode('canvas');
            });
        }

        if (this.chatModelModalEl) {
            const closeBtn = document.getElementById('closeChatModelModalBtn');
            const backdrop = this.chatModelModalEl.querySelector('.modal-backdrop');

            if (closeBtn) {
                closeBtn.addEventListener('click', () => this.hideChatModelModal());
            }

            if (backdrop) {
                backdrop.addEventListener('click', () => this.hideChatModelModal());
            }
        }

        if (this.chatHistory.length === 0) {
            this.showSystemMessage('Start chatting to sync responses with your canvas thread in real time.');
        }

        this.updateChatModelInfo();
        this.renderChatModelToggles();
    }

    setChatSidebarCollapsed(collapsed) {
        const normalized = Boolean(collapsed);
        if (this.chatSidebarCollapsed !== normalized) {
            this.chatSidebarCollapsed = normalized;
            try {
                localStorage.setItem('ui_chat_sidebar_collapsed', normalized ? 'true' : 'false');
            } catch (storageError) {
                console.warn('Unable to persist sidebar preference:', storageError);
            }
        }
        this.applyChatSidebarState();
    }

    applyChatSidebarState() {
        const collapsed = Boolean(this.chatSidebarCollapsed);
        if (this.chatSidebarEl) {
            this.chatSidebarEl.classList.toggle('is-collapsed', collapsed);
            this.chatSidebarEl.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
        }
        if (this.chatSidebarCollapseBtn) {
            this.chatSidebarCollapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            this.chatSidebarCollapseBtn.setAttribute(
                'aria-label',
                collapsed ? 'Show conversation threads' : 'Hide conversation threads'
            );
            this.chatSidebarCollapseBtn.textContent = collapsed ? '⟩' : '⟨';
        }
        if (this.chatSidebarShowBtn) {
            this.chatSidebarShowBtn.classList.toggle('is-visible', collapsed);
            this.chatSidebarShowBtn.setAttribute('tabindex', collapsed ? '0' : '-1');
            this.chatSidebarShowBtn.setAttribute('aria-hidden', collapsed ? 'false' : 'true');
        }
        if (this.chatContentEl) {
            this.chatContentEl.classList.toggle('sidebar-collapsed', collapsed);
        }
    }

    showSystemMessage(content) {
        this.renderChatMessage({ role: 'system', content });
    }

    async requestChatCompletion() {
        if (this.isChatLoading) {
            return;
        }

        if (!this.aiManager) {
            this.showSystemMessage('AI services are still initializing. Please try again in a moment.');
            return;
        }

        if (!Array.isArray(this.chatHistory) || this.chatHistory.length === 0) {
            this.showSystemMessage('Add a message to start the conversation.');
            return;
        }

        const lastMessage = this.chatHistory[this.chatHistory.length - 1];
        if (!lastMessage || lastMessage.role !== 'user') {
            this.showSystemMessage('Enter a user message to continue the conversation.');
            return;
        }

        const activeDescriptors = this.aiManager.getActiveTextModelDescriptors();
        if (!activeDescriptors || activeDescriptors.length === 0) {
            this.showSystemMessage('No active chat models configured. Enable at least one model to continue.');
            return;
        }

        const historySnapshot = Array.isArray(this.chatHistory)
            ? this.chatHistory.map(entry => ({ ...entry }))
            : [];

        const placeholderMap = new Map();
        const descriptorMap = new Map();
        activeDescriptors.forEach(descriptor => {
            descriptorMap.set(descriptor.key, descriptor);
        });

        const modelDisplayName = (model) => {
            if (typeof model !== 'string') return 'Model';
            const parts = model.split('/');
            return parts[parts.length - 1] || model;
        };

        const getLabel = (payload) => {
            if (payload && typeof payload.label === 'string' && payload.label.trim().length > 0) {
                return payload.label.trim();
            }

            const key = payload && typeof payload.modelKey === 'string' ? payload.modelKey : null;
            if (key && descriptorMap.has(key)) {
                const descriptor = descriptorMap.get(key);
                if (descriptor && descriptor.label) {
                    return descriptor.label;
                }
            }

            return modelDisplayName(payload?.model);
        };

        try {
            this.setChatLoading(true);

            activeDescriptors.forEach((descriptor, index) => {
                const placeholderText = `Generating with ${descriptor.label || modelDisplayName(descriptor.name)}…`;
                const node = this.appendChatConversationMessage('assistant', placeholderText, {
                    model: descriptor.label || descriptor.name,
                    select: index === 0
                });

                if (node) {
                    node.isStreaming = true;
                    placeholderMap.set(descriptor.key, node);
                    if (node.id) {
                        this.markChatMessageStreaming(node.id, true);
                    }
                }
            });

            const responses = await this.aiManager.generateChatResponse(historySnapshot, {
                onModelUpdate: async (update) => {
                    const lookupKey = update.modelKey || update.model;
                    const targetNode = placeholderMap.get(lookupKey);
                    if (!targetNode) {
                        return;
                    }

                    if (update.success) {
                        const content = typeof update.full === 'string'
                            ? update.full
                            : (typeof update.delta === 'string'
                                ? `${targetNode.text || ''}${update.delta}`
                                : targetNode.text || '');

                        this.updateStreamingChatNode(targetNode, content, {
                            done: Boolean(update.done)
                        });

                        if (targetNode.id) {
                            this.markChatMessageStreaming(targetNode.id, !update.done);
                        }

                        if (update.done) {
                            this.updateBranchState({ preserveBase: true });
                            this.renderBranchNavigation();
                            this.updateChatModelInfo();
                            this.refreshAllMessageActions();
                        }
                    } else if (update.errorMessage) {
                        const friendlyError = `⚠️ ${update.errorMessage}`;
                        this.updateStreamingChatNode(targetNode, friendlyError, {
                            done: true,
                            error: true
                        });
                        if (targetNode.id) {
                            this.markChatMessageStreaming(targetNode.id, false);
                        }
                        this.updateBranchState({ preserveBase: true });
                        this.renderBranchNavigation();
                        this.updateChatModelInfo();
                        this.refreshAllMessageActions();
                    }
                },
                onModelComplete: async (result) => {
                    const lookupKey = result.modelKey || result.model;
                    const targetNode = placeholderMap.get(lookupKey);
                    if (!targetNode) {
                        return;
                    }

                    if (targetNode.id) {
                        this.markChatMessageStreaming(targetNode.id, false);
                    }

                    if (result.success) {
                        this.updateStreamingChatNode(targetNode, result.content || '', {
                            done: true
                        });
                    } else {
                        const message = result.content || 'Generation failed';
                        this.updateStreamingChatNode(targetNode, `⚠️ ${message}`, {
                            done: true,
                            error: true
                        });
                        this.showSystemMessage(`${getLabel(result)} error: ${message}`);
                    }

                    this.updateBranchState({ preserveBase: true });
                    this.renderBranchNavigation();
                    this.updateChatModelInfo();
                    this.refreshAllMessageActions();
                }
            });

            const successfulResponse = Array.isArray(responses) && responses.some(item => item && item.success);
            if (!successfulResponse) {
                this.showSystemMessage('All models failed to respond.');
            }
        } catch (error) {
            console.error('❌ Chat error:', error);
            const friendlyMessage = getErrorMessage(error);
            this.showSystemMessage(friendlyMessage);
        } finally {
            this.setChatLoading(false);
            this.scrollChatToBottom();
        }
    }

    handleChatResponses(responses) {
        if (Array.isArray(responses) && responses.length > 0) {
            let successfulResponse = false;
            let firstResponseHandled = false;

            responses.forEach((result) => {
                if (result && result.success && result.content) {
                    successfulResponse = true;
                    const select = !firstResponseHandled;
                    this.appendChatConversationMessage('assistant', result.content, {
                        model: result.model,
                        select
                    });
                    if (select) {
                        firstResponseHandled = true;
                    }
                } else if (result && !result.success) {
                    const label = result.model ? `${result.model} error` : 'Model error';
                    this.showSystemMessage(`${label}: ${result.content || 'Unknown error'}`);
                }
            });

            if (!successfulResponse) {
                this.showSystemMessage('All models failed to respond.');
            }
            return;
        }

        if (typeof responses === 'string' && responses.trim().length > 0) {
            const fallbackModel = this.aiManager ? this.aiManager.getPrimaryTextModel() : null;
            this.appendChatConversationMessage('assistant', responses, { model: fallbackModel });
            return;
        }

        this.showSystemMessage('Received empty response from AI.');
    }

    renderChatMessage(message, { autoScroll = true } = {}) {
        if (!message || !this.chatMessagesContainer) return;

        const { role, content, model, nodeId, sequence } = message;
        const safeContent = typeof content === 'string' ? content.trim() : '';

        if (!safeContent) {
            return;
        }

        const messageEl = document.createElement('div');
        messageEl.classList.add('chat-message', role);
        if (nodeId) {
            messageEl.dataset.nodeId = nodeId.toString();
        }
        if (typeof sequence === 'number') {
            messageEl.dataset.sequence = sequence.toString();
        }
        messageEl.dataset.role = role;

        if (role === 'assistant' && model) {
            const modelEl = document.createElement('div');
            modelEl.className = 'chat-message-model';
            modelEl.textContent = model;
            messageEl.appendChild(modelEl);
        }

        const textEl = document.createElement('div');
        textEl.className = 'chat-message-text';
        textEl.innerHTML = this.markdownToHtml(safeContent, 16);
        messageEl.appendChild(textEl);

        this.attachChatMessageActions(messageEl, message);

        this.chatMessagesContainer.appendChild(messageEl);
        if (autoScroll) {
            this.scrollChatToBottom();
        }
    }

    markChatMessageStreaming(nodeId, isStreaming) {
        if (!nodeId || !this.chatMessagesContainer) {
            return;
        }

        const selector = `[data-node-id="${nodeId}"]`;
        const messageEl = this.chatMessagesContainer.querySelector(selector);
        if (!messageEl) {
            return;
        }

        messageEl.classList.toggle('chat-message-streaming', Boolean(isStreaming));
    }

    updateStreamingChatNode(node, content, { done = false, error = false } = {}) {
        if (!node || typeof content !== 'string') {
            return;
        }

        node.text = content;
        node.isStreaming = !done;
        const baseHeight = 140;
        const extraHeight = Math.max(0, content.length - 220) * 0.4;
        node.height = Math.min(baseHeight + extraHeight, 480);

        const nodeId = node.id ? node.id.toString() : null;

        if (nodeId && this.chatMessagesContainer) {
            const selector = `[data-node-id="${nodeId}"]`;
            const messageEl = this.chatMessagesContainer.querySelector(selector);
            if (messageEl) {
                const textEl = messageEl.querySelector('.chat-message-text');
                if (textEl) {
                    textEl.innerHTML = this.markdownToHtml(content, 16);
                }
                messageEl.classList.toggle('chat-message-streaming', !done);
                messageEl.classList.toggle('chat-message-error', Boolean(error));
                
                // Update action buttons when streaming is complete
                if (done) {
                    this.refreshMessageActions(messageEl, nodeId);
                }
            }
        }

        if (nodeId && Array.isArray(this.chatHistory)) {
            const historyEntry = this.chatHistory.find(item => item && item.nodeId === nodeId);
            if (historyEntry) {
                historyEntry.content = content;
            }
        }

        if (done && this.canvasState) {
            this.canvasState.saveToLocalStorage();
        }

        this.scrollChatToBottom();
    }

    refreshMessageActions(messageEl, nodeId) {
        if (!messageEl || !nodeId) return;

        const role = messageEl.dataset.role;
        if (role !== 'user' && role !== 'assistant') return;

        // Remove existing actions
        const existingActions = messageEl.querySelector('.chat-message-actions');
        if (existingActions) {
            existingActions.remove();
        }

        // Re-attach actions with updated navigation info
        const message = {
            nodeId,
            role,
            content: messageEl.querySelector('.chat-message-text')?.textContent || '',
            model: messageEl.querySelector('.chat-message-model')?.textContent || null
        };

        this.attachChatMessageActions(messageEl, message);
    }

    refreshAllMessageActions() {
        if (!this.chatMessagesContainer) return;

        const allMessages = this.chatMessagesContainer.querySelectorAll('.chat-message[data-node-id]');
        allMessages.forEach(messageEl => {
            const nodeId = messageEl.dataset.nodeId;
            const role = messageEl.dataset.role;
            
            if (nodeId && (role === 'user' || role === 'assistant')) {
                this.refreshMessageActions(messageEl, nodeId);
            }
        });
    }

    attachChatMessageActions(messageEl, message) {
        if (!messageEl || !message) return;

        const { nodeId, role } = message;
        if (!nodeId || (role !== 'user' && role !== 'assistant')) {
            return;
        }

        const actionsEl = document.createElement('div');
        actionsEl.className = 'chat-message-actions';

        // Add branch navigation for assistant messages
        if (role === 'assistant') {
            const navInfo = this.getMessageNavigationInfo(nodeId);
            if (navInfo && navInfo.siblings.length > 1) {
                const prevBtn = document.createElement('button');
                prevBtn.type = 'button';
                prevBtn.className = 'chat-message-action-btn branch-nav-inline';
                prevBtn.innerHTML = '◀';
                prevBtn.title = 'Previous response';
                prevBtn.addEventListener('click', () => {
                    this.navigateToMessageSibling(nodeId, -1);
                });
                actionsEl.appendChild(prevBtn);

                const navLabel = document.createElement('span');
                navLabel.className = 'chat-message-nav-label';
                navLabel.textContent = `Response ${navInfo.position}/${navInfo.total}`;
                actionsEl.appendChild(navLabel);

                const nextBtn = document.createElement('button');
                nextBtn.type = 'button';
                nextBtn.className = 'chat-message-action-btn branch-nav-inline';
                nextBtn.innerHTML = '▶';
                nextBtn.title = 'Next response';
                nextBtn.addEventListener('click', () => {
                    this.navigateToMessageSibling(nodeId, 1);
                });
                actionsEl.appendChild(nextBtn);
            }
        }

        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'chat-message-action-btn';
        editBtn.textContent = 'Edit';
        editBtn.addEventListener('click', () => {
            this.beginChatMessageEdit(nodeId);
        });
        actionsEl.appendChild(editBtn);

        const rerunBtn = document.createElement('button');
        rerunBtn.type = 'button';
        rerunBtn.className = 'chat-message-action-btn';
        rerunBtn.textContent = 'Rerun';
        rerunBtn.addEventListener('click', () => {
            this.rerunChatFromNode(nodeId);
        });
        actionsEl.appendChild(rerunBtn);

        messageEl.appendChild(actionsEl);
    }

    beginChatMessageEdit(nodeId) {
        if (!nodeId || !this.chatMessagesContainer) {
            return;
        }

        const existingEdit = this.activeChatEdit;
        if (existingEdit && existingEdit.nodeId === nodeId) {
            return;
        }

        if (existingEdit) {
            this.cancelChatMessageEdit(existingEdit.nodeId);
        }

        const messageSelector = `[data-node-id="${nodeId}"]`;
        const messageEl = this.chatMessagesContainer.querySelector(messageSelector);
        if (!messageEl) {
            return;
        }

        const role = messageEl.dataset.role;
        if (role !== 'user' && role !== 'assistant') {
            return;
        }

        const node = this.getChatNodeById(nodeId);
        if (!node) {
            this.showSystemMessage('Original message could not be found.');
            return;
        }

        const textEl = messageEl.querySelector('.chat-message-text');
        if (!textEl) {
            return;
        }

        const actionsEl = messageEl.querySelector('.chat-message-actions');

        const editorContainer = document.createElement('div');
        editorContainer.className = 'chat-message-editor';

        const textarea = document.createElement('textarea');
        textarea.className = 'chat-message-editor-input';
        textarea.value = node.text || '';
        textarea.setAttribute('aria-label', 'Edit message');
        editorContainer.appendChild(textarea);

        const controls = document.createElement('div');
        controls.className = 'chat-message-editor-actions';

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'chat-message-editor-save';
        saveBtn.textContent = 'Save';
        saveBtn.addEventListener('click', () => this.applyChatMessageEdit(nodeId, textarea.value));

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'chat-message-editor-cancel';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => this.cancelChatMessageEdit(nodeId));

        controls.appendChild(saveBtn);
        controls.appendChild(cancelBtn);
        editorContainer.appendChild(controls);

        textarea.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.cancelChatMessageEdit(nodeId);
            } else if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                this.applyChatMessageEdit(nodeId, textarea.value);
            }
        });

        textarea.addEventListener('input', () => {
            this.autosizeTextarea(textarea, 12);
        });

        textEl.style.display = 'none';
        if (actionsEl) {
            actionsEl.style.display = 'none';
        }

        messageEl.classList.add('editing');
        messageEl.appendChild(editorContainer);

        this.autosizeTextarea(textarea, 12);
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);

        this.activeChatEdit = {
            nodeId,
            messageEl,
            textarea,
            textEl,
            actionsEl,
            editorEl: editorContainer
        };
    }

    cancelChatMessageEdit(nodeId) {
        if (!this.activeChatEdit) {
            return;
        }

        if (nodeId && this.activeChatEdit.nodeId !== nodeId) {
            return;
        }

        const { messageEl, textEl, actionsEl, editorEl } = this.activeChatEdit;
        if (editorEl && editorEl.parentNode) {
            editorEl.parentNode.removeChild(editorEl);
        }

        if (textEl) {
            textEl.style.display = '';
        }

        if (actionsEl) {
            actionsEl.style.display = '';
        }

        if (messageEl) {
            messageEl.classList.remove('editing');
        }

        this.activeChatEdit = null;
    }

    applyChatMessageEdit(nodeId, newContent) {
        if (!this.activeChatEdit || (nodeId && this.activeChatEdit.nodeId !== nodeId)) {
            return;
        }

        const trimmedContent = typeof newContent === 'string' ? newContent.trim() : '';
        if (trimmedContent.length === 0) {
            this.showSystemMessage('Message cannot be empty.');
            return;
        }

        const node = this.getChatNodeById(nodeId);
        if (!node) {
            this.showSystemMessage('Original message could not be found.');
            return;
        }

        try {
            this.canvasState.saveState();
        } catch (error) {
            console.warn('Unable to capture history before editing chat message:', error);
        }

        node.text = trimmedContent;
        const extraHeight = Math.max(0, trimmedContent.length - 180) * 0.5;
        node.height = Math.min(140 + extraHeight, 360);

        this.canvasState.saveToLocalStorage();
        this.activeChatEdit = null;
        this.updateBranchState({ preserveBase: true });
        this.renderChatFromBranch();
    }

    getChatNodeById(nodeId) {
        if (!this.canvasState || !nodeId) {
            return null;
        }

        const matchId = nodeId.toString();
        return this.canvasState.nodes.find(node => node && node.id && node.id.toString() === matchId) || null;
    }

    autosizeTextarea(textarea, maxRows = 7) {
        if (!textarea) {
            return;
        }

        const computed = window.getComputedStyle(textarea);
        const lineHeight = parseFloat(computed.lineHeight || '20');
        const padding = parseFloat(computed.paddingTop || '0') + parseFloat(computed.paddingBottom || '0');
        const border = parseFloat(computed.borderTopWidth || '0') + parseFloat(computed.borderBottomWidth || '0');
        const maxHeight = (lineHeight * maxRows) + padding + border;

        textarea.style.height = 'auto';
        const newHeight = Math.min(maxHeight, textarea.scrollHeight + border);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight + border > maxHeight ? 'auto' : 'hidden';
    }

    clearChatHistory() {
        this.chatHistory = [];

        if (this.chatMessagesContainer) {
            this.chatMessagesContainer.innerHTML = '';
            this.showSystemMessage('Chat cleared. Start a new conversation whenever you are ready.');
            this.renderBranchNavigation();
        }
    }

    renderChatModelToggles() {
        if (!this.chatModelToggleEl) {
            this.chatModelToggleEl = document.getElementById('chatModelToggles');
        }

        if (!this.chatModelToggleEl) return;

        this.chatModelToggleEl.innerHTML = '';

        if (!this.aiManager) {
            this.chatModelToggleEl.style.display = 'flex';
            const placeholder = document.createElement('div');
            placeholder.className = 'chat-model-toggle-empty';
            placeholder.textContent = 'Models unavailable';
            this.chatModelToggleEl.appendChild(placeholder);
            return;
        }

        const descriptors = this.aiManager.getTextModelDescriptors();

        if (descriptors.length === 0) {
            this.chatModelToggleEl.style.display = 'flex';
            const placeholder = document.createElement('div');
            placeholder.className = 'chat-model-toggle-empty';
            placeholder.textContent = 'No models configured';
            this.chatModelToggleEl.appendChild(placeholder);
            return;
        }

        this.chatModelToggleEl.style.display = 'flex';
        descriptors.forEach((descriptor, idx) => {
            const row = document.createElement('div');
            row.className = 'chat-model-toggle-row';
            if (descriptor.hasCustomPrompt) {
                row.classList.add('has-custom-prompt');
            }

            const toggleLabel = document.createElement('label');
            toggleLabel.className = 'chat-model-toggle';
            const toggleId = `chat_model_toggle_${this.sanitizeDomId(descriptor.key || `${descriptor.name}_${idx}`, `${idx}`)}`;
            toggleLabel.htmlFor = toggleId;

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = toggleId;
            checkbox.checked = descriptor.isActive;

            checkbox.addEventListener('change', () => {
                this.aiManager.setModelActive(descriptor, checkbox.checked);
                this.updateModelSelectorUI();
            });

            const orderBadge = document.createElement('span');
            orderBadge.className = 'chat-model-order';
            orderBadge.textContent = idx + 1;

            const labelText = document.createElement('span');
            labelText.textContent = descriptor.label;
            labelText.style.flex = '1';
            labelText.style.wordBreak = 'break-word';
            labelText.style.whiteSpace = 'normal';

            toggleLabel.appendChild(checkbox);
            toggleLabel.appendChild(orderBadge);
            toggleLabel.appendChild(labelText);

            if (descriptor.hasCustomPrompt) {
                const badge = document.createElement('span');
                badge.className = 'chat-model-prompt-badge';
                badge.textContent = 'Custom';
                toggleLabel.appendChild(badge);
            }

            const configBtn = document.createElement('button');
            configBtn.type = 'button';
            configBtn.className = 'chat-model-config-btn';
            configBtn.textContent = '📝';
            configBtn.title = descriptor.hasCustomPrompt
                ? `Edit custom prompt for ${descriptor.label}`
                : `Add custom prompt for ${descriptor.label}`;
            configBtn.setAttribute('aria-label', configBtn.title);
            if (descriptor.hasCustomPrompt) {
                configBtn.classList.add('has-custom-prompt');
            }

            configBtn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.showModelPromptModal(descriptor);
            });

            row.appendChild(toggleLabel);
            row.appendChild(configBtn);
            this.chatModelToggleEl.appendChild(row);
        });
    }

    setChatLoading(isLoading) {
        this.isChatLoading = Boolean(isLoading);

        if (this.chatInputEl) {
            this.chatInputEl.disabled = isLoading;
        }

        if (this.chatSendBtnEl) {
            this.chatSendBtnEl.disabled = isLoading;
            this.chatSendBtnEl.textContent = isLoading ? 'Sending…' : this.chatSendBtnDefaultLabel;
        }
    }

    showChatModelModal() {
        if (!this.chatModelModalEl) {
            return;
        }

        this.chatModelModalEl.classList.remove('hidden');
        this.renderChatModelToggles();
        this.updateChatModelInfo();

        if (!this.chatModelModalKeyHandler) {
            this.chatModelModalKeyHandler = (event) => {
                if (event.key === 'Escape') {
                    this.hideChatModelModal();
                }
            };
            document.addEventListener('keydown', this.chatModelModalKeyHandler);
        }
    }

    hideChatModelModal() {
        if (!this.chatModelModalEl) {
            return;
        }

        if (this.chatModelModalEl.classList.contains('hidden')) {
            return;
        }

        this.chatModelModalEl.classList.add('hidden');

        if (this.chatModelModalKeyHandler) {
            document.removeEventListener('keydown', this.chatModelModalKeyHandler);
            this.chatModelModalKeyHandler = null;
        }
    }

    showChatTypingIndicator() {
        if (!this.chatMessagesContainer) return null;

        const indicator = document.createElement('div');
        indicator.classList.add('chat-message', 'assistant', 'typing-indicator');
        const textEl = document.createElement('div');
        textEl.className = 'chat-message-text';
        textEl.textContent = 'AI is thinking…';
        indicator.appendChild(textEl);
        this.chatMessagesContainer.appendChild(indicator);
        this.scrollChatToBottom();
        return indicator;
    }

    removeChatTypingIndicator(indicator) {
        if (indicator && indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
        }
    }

    handleChatScroll() {
        if (!this.chatMessagesContainer) {
            return;
        }

        const container = this.chatMessagesContainer;
        const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
        this.chatAutoScroll = distanceFromBottom <= this.chatScrollUnlockOffset;

        if (this.chatCurrentBranchKey) {
            if (this.chatAutoScroll) {
                delete this.chatScrollPositions[this.chatCurrentBranchKey];
            } else {
                this.chatScrollPositions[this.chatCurrentBranchKey] = container.scrollTop;
            }
        }
    }

    isChatNearBottom(offset = this.chatScrollUnlockOffset) {
        if (!this.chatMessagesContainer) {
            return true;
        }

        const container = this.chatMessagesContainer;
        const effectiveOffset = typeof offset === 'number' ? offset : 0;
        const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
        return distanceFromBottom <= effectiveOffset;
    }

    scrollChatToBottom({ force = false } = {}) {
        if (!this.chatMessagesContainer) {
            return;
        }

        if (!force && !this.chatAutoScroll) {
            return;
        }

        const container = this.chatMessagesContainer;
        const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
        container.scrollTop = maxScrollTop;

        if (force) {
            this.chatAutoScroll = true;
        }
    }

    saveCurrentChatScrollPosition() {
        if (!this.chatMessagesContainer || !this.chatCurrentBranchKey) {
            return;
        }

        this.chatScrollPositions[this.chatCurrentBranchKey] = this.chatMessagesContainer.scrollTop;
    }

    restoreChatScrollPosition({ fallbackToBottom = true } = {}) {
        if (!this.chatMessagesContainer) {
            return;
        }

        const container = this.chatMessagesContainer;

        if (this.chatShouldScrollToTop) {
            container.scrollTop = 0;
            this.chatAutoScroll = false;
            this.chatShouldScrollToTop = false;
            return;
        }

        const key = this.chatCurrentBranchKey;
        if (key && typeof this.chatScrollPositions[key] === 'number') {
            const saved = this.chatScrollPositions[key];
            const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
            container.scrollTop = Math.max(0, Math.min(saved, maxScrollTop));
            this.chatAutoScroll = false;
            return;
        }

        if (fallbackToBottom) {
            this.scrollChatToBottom({ force: true });
        }
    }

    computeChatBranchKey(nodes = []) {
        if (!Array.isArray(nodes) || nodes.length === 0) {
            return null;
        }

        return nodes
            .map(node => (node && node.id !== undefined && node.id !== null ? node.id : 'unknown'))
            .join('>');
    }

    initializeChatCanvasState() {
        if (!this.canvasState) return;

        if (!this.chatUIInitialized) {
            this.setupChatUI();
        }

        this.normalizeThreadMetadata();
        this.ensureActiveCanvasThread({ createIfMissing: false });

        const chatNodes = this.canvasState.nodes
            .filter(node => node && node.isChatMessage)
            .sort((a, b) => {
                const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : (a.y || 0);
                const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : (b.y || 0);
                return seqA - seqB;
            });

        if (chatNodes.length === 0) {
            this.chatMessageIndex = 0;
            this.lastChatNode = null;
            this.renderChatTree();
            return;
        }

        const firstNode = chatNodes[0];
        const firstOffset = firstNode.chatRole === 'user' ? this.chatUserOffsetX : this.chatAssistantOffsetX;
        this.chatNodeBaseX = firstNode.x - firstOffset;
        this.chatNodeBaseY = firstNode.y;
        try {
            localStorage.setItem('ui_chat_base_x', this.chatNodeBaseX.toString());
            localStorage.setItem('ui_chat_base_y', this.chatNodeBaseY.toString());
        } catch (storageError) {
            console.warn('Unable to persist chat base position:', storageError);
        }

        const maxSequence = chatNodes.reduce((acc, node) => {
            const seq = typeof node.chatSequence === 'number' ? node.chatSequence : acc;
            return seq > acc ? seq : acc;
        }, -1);
        this.chatMessageIndex = maxSequence >= 0 ? maxSequence + 1 : chatNodes.length;

        this.lastChatNode = chatNodes[chatNodes.length - 1];

        // If we're currently in chat mode with no active branch loaded, hydrate it now
        if (this.currentViewMode === 'chat' && this.chatBranchNodes.length === 0) {
            const latestNode = [...chatNodes]
                .sort((a, b) => {
                    const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : (a.y || 0);
                    const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : (b.y || 0);
                    return seqA - seqB;
                })
                .pop();

            if (latestNode) {
                const branch = this.buildBranchFromNode(latestNode);
                if (branch && branch.length > 0) {
                    this.chatBranchNodes = branch;
                    this.chatFocusNodeId = latestNode.id;
                    this.updateBranchState({ preserveBase: false });
                    this.renderChatFromBranch();
                    this.updateChatModelInfo();
                    this.restoreChatScrollPosition({ fallbackToBottom: true });
                }
            }
        }

        if (!this.chatBranchNodes || this.chatBranchNodes.length === 0) {
            this.renderChatTree();
        }
    }

    enterChatForSelectedNode() {
        if (!this.canvasState) return;

        if (this.canvasState.selectedNodes.length !== 1) {
            this.showNotification('Please select a single node to open chat mode.', 'warning');
            return;
        }

        const node = this.canvasState.selectedNodes[0];
        this.enterChatForNode(node);
    }

    enterChatForNode(node) {
        if (!node) {
            this.showNotification('Unable to open chat for this node.', 'error');
            return;
        }

        const threadRoot = this.findThreadRoot(node);
        if (threadRoot && threadRoot.id) {
            const threadId = threadRoot.id.toString();
            this.canvasState.setActiveThread(threadId);
            this.activeCanvasThreadId = threadId;
        }

        const branchNodes = this.buildBranchFromNode(node);
        if (!branchNodes || branchNodes.length === 0) {
            this.showNotification('No branch data available for this node.', 'warning');
            return;
        }

        this.chatFocusNodeId = node.id;
        this.chatBranchNodes = branchNodes;
        this.setViewMode('chat');

        this.updateBranchState({ preserveBase: false });
        this.renderChatFromBranch();
        this.updateChatModelInfo();
    }

    findThreadRoot(node) {
        if (!node || !this.canvasState) {
            return null;
        }

        let current = node;
        let lastValid = node;
        const visited = new Set();

        while (current && !visited.has(current.id)) {
            if (current.hideChatMessage) {
                return current;
            }
            visited.add(current.id);
            lastValid = current;
            current = this.getParentNode(current);
        }

        return lastValid || null;
    }

    computeNextChatThreadPlacement() {
        if (!this.canvasState) {
            return { rootX: this.chatNodeBaseX + this.chatAssistantOffsetX, rootY: this.chatNodeBaseY, label: 'Chat Thread 1', threadIndex: 1 };
        }

        const existingChatNodes = (this.canvasState.nodes || []).filter(node => node && node.isChatMessage);
        const threadIndex = existingChatNodes.filter(node => node && node.hideChatMessage).length + 1;

        let rootX = this.chatNodeBaseX + this.chatAssistantOffsetX;
        let rootY = this.chatNodeBaseY;

        const canvasEl = document.getElementById('canvas');
        if (canvasEl) {
            const rect = canvasEl.getBoundingClientRect();
            const scale = typeof this.canvasState.scale === 'number' ? this.canvasState.scale : 1;
            const offsetX = typeof this.canvasState.offsetX === 'number' ? this.canvasState.offsetX : 0;
            const offsetY = typeof this.canvasState.offsetY === 'number' ? this.canvasState.offsetY : 0;

            rootX = (rect.width / 2 - offsetX) / scale;
            rootY = (rect.height / 2 - offsetY) / scale;
        }

        const label = `Chat Thread ${threadIndex}`;

        return { rootX, rootY, label, threadIndex };
    }

    createChatThreadRoot({ label = null, persist = true } = {}) {
        if (!this.canvasState) {
            return null;
        }

        const placement = this.computeNextChatThreadPlacement();
        const rootLabel = label || placement.label;

        const rootNode = this.canvasState.createNode(rootLabel, placement.rootX, placement.rootY, null, { save: false });
        rootNode.isChatMessage = true;
        rootNode.chatRole = 'system';
        rootNode.hideChatMessage = true;
        rootNode.chatSequence = this.chatMessageIndex++;
        rootNode.width = 360;
        rootNode.height = 140;
        if (rootNode.id) {
            const threadId = rootNode.id.toString();
            rootNode.threadId = threadId;
            this.canvasState.setActiveThread(threadId);
            this.activeCanvasThreadId = threadId;
        }

        if (persist) {
            this.canvasState.saveState();
            this.canvasState.saveToLocalStorage();
        }

        console.info('[ChatDebug] Created chat thread root node.', {
            nodeId: rootNode.id,
            label: rootLabel,
            placement
        });

        return rootNode;
    }

    normalizeThreadMetadata() {
        if (!this.canvasState) {
            return;
        }

        const visited = new Set();
        const roots = this.getChatThreadRoots();
        roots.forEach(root => {
            if (!root || !root.id) {
                return;
            }
            const threadId = root.id.toString();
            this.assignThreadToBranch(root, threadId, visited);
        });
    }

    assignThreadToBranch(node, threadId, visited = new Set()) {
        if (!node || !threadId) {
            return;
        }

        const key = node.id ? node.id.toString() : null;
        if (!key || visited.has(key)) {
            return;
        }

        visited.add(key);
        node.threadId = threadId;

        const children = this.getChildNodes(node);
        children.forEach(child => this.assignThreadToBranch(child, threadId, visited));
    }

    getNodesForThread(threadId) {
        if (!this.canvasState || !threadId) {
            return [];
        }

        const normalized = threadId.toString();
        const nodes = Array.isArray(this.canvasState.nodes) ? this.canvasState.nodes : [];
        const directMatches = nodes.filter(node => {
            if (!node || !node.id) {
                return false;
            }
            const nodeId = node.id.toString();
            if (nodeId === normalized) {
                return true;
            }
            if (node.threadId && node.threadId.toString() === normalized) {
                return true;
            }
            return false;
        });

        if (directMatches.length > 0) {
            return directMatches;
        }

        const nodeMap = new Map();
        nodes.forEach(node => {
            if (node && node.id) {
                nodeMap.set(node.id.toString(), node);
            }
        });

        const root = nodeMap.get(normalized);
        if (!root) {
            return [];
        }

        const visited = new Set([normalized]);
        const results = [root];
        const queue = [root];
        const connections = Array.isArray(this.canvasState.connections) ? this.canvasState.connections : [];

        while (queue.length > 0) {
            const current = queue.shift();
            const currentId = current && current.id ? current.id.toString() : null;
            if (!currentId) {
                continue;
            }

            connections.forEach(connection => {
                const fromId = connection.fromNode || connection.from;
                const toId = connection.toNode || connection.to;
                if (!fromId || !toId) {
                    return;
                }

                const normalizedFrom = fromId.toString();
                const normalizedTo = toId.toString();

                if (normalizedFrom === currentId && !visited.has(normalizedTo)) {
                    const target = nodeMap.get(normalizedTo);
                    if (target) {
                        visited.add(normalizedTo);
                        queue.push(target);
                        results.push(target);
                    }
                } else if (normalizedTo === currentId && !visited.has(normalizedFrom)) {
                    const target = nodeMap.get(normalizedFrom);
                    if (target) {
                        visited.add(normalizedFrom);
                        queue.push(target);
                        results.push(target);
                    }
                }
            });
        }

        return results;
    }

    getChatThreadRoots() {
        if (!this.canvasState || !Array.isArray(this.canvasState.nodes)) {
            return [];
        }

        return this.canvasState.nodes
            .filter(node => node && node.isChatMessage && node.hideChatMessage)
            .sort((a, b) => {
                const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : (a.y || 0);
                const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : (b.y || 0);
                if (seqA !== seqB) {
                    return seqA - seqB;
                }
                return (a.y || 0) - (b.y || 0);
            });
    }

    ensureActiveCanvasThread({ createIfMissing = false } = {}) {
        if (!this.canvasState) {
            return null;
        }

        this.normalizeThreadMetadata();

        if (this.canvasState.activeThreadId) {
            const existing = this.canvasState.nodes.find(node => node && node.id && node.id.toString() === this.canvasState.activeThreadId);
            if (existing) {
                this.activeCanvasThreadId = existing.id.toString();
                return existing;
            }
            this.canvasState.setActiveThread(null);
        }

        const roots = this.getChatThreadRoots();
        if (roots.length > 0) {
            const root = roots[0];
            if (root && root.id) {
                this.canvasState.setActiveThread(root.id.toString());
                this.activeCanvasThreadId = root.id.toString();
                return root;
            }
        }

        if (createIfMissing) {
            const newRoot = this.createChatThreadRoot();
            if (newRoot && newRoot.id) {
                const threadId = newRoot.id.toString();
                this.canvasState.setActiveThread(threadId);
                this.activeCanvasThreadId = threadId;
            }
            return newRoot || null;
        }

        return null;
    }

    ensureActiveChatBranch({ reason = 'unspecified' } = {}) {
        if (!this.canvasState) {
            return null;
        }

        if (Array.isArray(this.chatBranchNodes) && this.chatBranchNodes.length > 0) {
            const existingRoot = this.chatBranchNodes[0];
            const threadRoot = this.findThreadRoot(existingRoot);
            if (threadRoot && threadRoot.id) {
                const threadId = threadRoot.id.toString();
                this.canvasState.setActiveThread(threadId);
                this.activeCanvasThreadId = threadId;
            }
            return this.chatBranchNodes[0];
        }

        const rootNode = this.ensureActiveCanvasThread({ createIfMissing: true });
        if (!rootNode) {
            console.error('[ChatDebug] Failed to initialize chat branch.', { reason });
            return null;
        }

        this.chatBranchNodes = [rootNode];
        this.chatFocusNodeId = rootNode.id;
        this.lastChatNode = rootNode;

        this.updateBranchState({ preserveBase: false });

        if (this.currentViewMode !== 'chat') {
            this.setViewMode('chat');
        }

        this.renderChatFromBranch();
        this.updateChatModelInfo();

        console.info('[ChatDebug] Auto-initialized chat branch.', {
            reason,
            rootNodeId: rootNode.id
        });

        return rootNode;
    }

    startNewChat() {
        if (!this.canvasState) return;

        const rootNode = this.createChatThreadRoot();
        if (!rootNode) {
            this.showNotification('Unable to create a new chat thread.', 'error');
            return;
        }

        this.enterChatForNode(rootNode);
    }

    appendChatConversationMessage(role, content, { model = null, select = true } = {}) {
        if (!content || !content.trim()) {
            return;
        }

        const wasNearBottom = this.isChatNearBottom();
        if (role === 'user') {
            this.chatAutoScroll = true;
        } else if (select && wasNearBottom) {
            this.chatAutoScroll = true;
        }

        const newNode = this.syncChatMessageToCanvas(role, content, { model, select });
        if (!newNode) {
            this.showSystemMessage('Unable to update the canvas branch. Please try again.');
            return;
        }
        if (role === 'assistant' && select === false) {
            this.updateBranchState({ preserveBase: true });
            this.renderBranchNavigation();
            this.updateChatModelInfo();
        } else {
            this.updateBranchState();
            this.renderChatFromBranch();
            this.updateChatModelInfo();
        }

        return newNode;
    }

    async rerunChatFromNode(nodeId) {
        if (!nodeId || !Array.isArray(this.chatBranchNodes) || this.chatBranchNodes.length === 0) {
            return;
        }

        const targetIndex = this.chatBranchNodes.findIndex(node => node && node.id && node.id.toString() === nodeId.toString());
        if (targetIndex === -1) {
            return;
        }

        const targetNode = this.chatBranchNodes[targetIndex];
        if (!targetNode) {
            return;
        }

        let rerunIndex = targetIndex;
        if (targetNode.chatRole === 'assistant') {
            rerunIndex = targetIndex - 1;
        }

        if (rerunIndex < 0) {
            this.showSystemMessage('No earlier user prompt available to rerun from.');
            return;
        }

        const rerunNode = this.chatBranchNodes[rerunIndex];
        if (!rerunNode || rerunNode.chatRole !== 'user') {
            this.showSystemMessage('Rerun is only available for user prompts.');
            return;
        }

        const branchIndex = this.chatBranchNodes.findIndex(node => node && node.id === rerunNode.id);
        if (branchIndex === -1) {
            return;
        }

        this.chatBranchNodes = this.chatBranchNodes.slice(0, branchIndex + 1);
        this.lastChatNode = rerunNode;
        this.chatFocusNodeId = rerunNode.id;

        this.updateBranchState();
        this.renderChatFromBranch();

        try {
            await this.requestChatCompletion();
        } catch (error) {
            console.error('❌ Chat rerun error:', error);
        }
    }

    updateBranchState({ preserveBase = true } = {}) {
        if (!this.chatBranchNodes || this.chatBranchNodes.length === 0) {
            this.chatHistory = [];
            this.chatBranchLevels = [];
            this.lastChatNode = null;
            this.renderChatTree();
            return;
        }

        const rootNode = this.chatBranchNodes[0];
        if (!preserveBase && rootNode) {
            const rootOffset = rootNode.chatRole === 'user' ? this.chatUserOffsetX : this.chatAssistantOffsetX;
            this.chatNodeBaseX = rootNode.x - rootOffset;
            this.chatNodeBaseY = rootNode.y;
            try {
                localStorage.setItem('ui_chat_base_x', this.chatNodeBaseX.toString());
                localStorage.setItem('ui_chat_base_y', this.chatNodeBaseY.toString());
            } catch (storageError) {
                console.warn('Unable to persist chat base position:', storageError);
            }
        }

        this.chatBranchLevels = this.chatBranchNodes.map((node, index) => {
            const parent = index > 0 ? this.chatBranchNodes[index - 1] : null;
            const siblings = parent ? this.getChildNodes(parent) : [];
            const siblingIndex = parent ? siblings.findIndex(item => item.id === node.id) : 0;
            return {
                node,
                siblings,
                siblingIndex: siblingIndex >= 0 ? siblingIndex : 0
            };
        });

        const maxSequence = this.chatBranchNodes.reduce((acc, node) => {
            if (typeof node.chatSequence === 'number') {
                return Math.max(acc, node.chatSequence);
            }
            return acc;
        }, -1);

        this.chatMessageIndex = maxSequence >= 0 ? maxSequence + 1 : this.chatBranchNodes.length;
        this.lastChatNode = this.chatBranchNodes[this.chatBranchNodes.length - 1];

        this.chatHistory = this.chatBranchNodes
            .map((node, index) => this.convertNodeToChatMessage(node, index))
            .filter(message => message && message.content && message.content.trim().length > 0);

        this.renderChatTree();
    }

    renderChatFromBranch() {
        if (!this.chatMessagesContainer) return;

        this.activeChatEdit = null;
        this.chatMessagesContainer.innerHTML = '';

        if (!this.chatHistory || this.chatHistory.length === 0) {
            this.showSystemMessage('This branch is empty. Start the conversation with a new message.');
        } else {
            this.chatHistory.forEach(message => {
                this.renderChatMessage(message, { autoScroll: false });
            });
        }

        this.scrollChatToBottom();
        this.renderBranchNavigation();
        this.updateChatModelInfo();
    }

    renderBranchNavigation() {
        if (!this.chatBranchNavEl) return;

        // Hide the global navigation bar since we now have per-message navigation
        this.chatBranchNavEl.style.display = 'none';
        this.chatBranchNavEl.innerHTML = '';
    }

    renderChatTree() {
        if (!this.canvasState) return;

        if (!this.chatTreeContainer) {
            this.chatTreeContainer = document.getElementById('chatTree');
            if (this.chatTreeContainer && !this.chatTreeScrollHandlerAttached) {
                this.chatTreeContainer.addEventListener('scroll', () => {
                    this.chatTreeScrollTop = this.chatTreeContainer.scrollTop;
                });
                this.chatTreeScrollHandlerAttached = true;
            }
        }

        if (!this.chatTreeContainer) return;

        const threads = this.buildChatThreads();
        const diagnostics = this.chatThreadDiagnostics;
        if ((!threads || threads.length === 0) && diagnostics && diagnostics.totalChatNodes > 0) {
            const anomalyKey = `anomaly:${diagnostics.totalChatNodes}:${diagnostics.connectionCount}:${diagnostics.rootCount}`;
            if (this.chatThreadDiagnosticsLogKey !== anomalyKey) {
                console.warn('[ChatDebug] Chat tree computed zero threads despite chat nodes present.', diagnostics);
                this.chatThreadDiagnosticsLogKey = anomalyKey;
            }
        } else if (diagnostics) {
            const summaryKey = `summary:${diagnostics.totalChatNodes}:${diagnostics.threadCount}`;
            if (this.chatThreadDiagnosticsLogKey !== summaryKey) {
                console.debug('[ChatDebug] Chat tree summary', diagnostics);
                this.chatThreadDiagnosticsLogKey = summaryKey;
            }
        }
        const focusId = this.chatFocusNodeId ? this.chatFocusNodeId.toString() : null;
        const activePathIds = new Set(
            (Array.isArray(this.chatBranchNodes) ? this.chatBranchNodes : [])
                .map(node => (node && node.id ? node.id.toString() : null))
                .filter(Boolean)
        );

        if (this.chatSidebarEl) {
            this.chatSidebarEl.classList.toggle('is-empty', !threads || threads.length === 0);
        }

        const previousScroll = typeof this.chatTreeScrollTop === 'number' ? this.chatTreeScrollTop : 0;

        this.chatTreeContainer.innerHTML = '';

        if (!threads || threads.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'chat-tree-empty';
            empty.textContent = 'No chat threads yet. Start a conversation!';
            this.chatTreeContainer.appendChild(empty);
            this.chatTreeLastFocusId = null;
            this.chatTreeScrollTop = 0;
            return;
        }

        const availableThreadIds = new Set();
        threads.forEach(thread => {
            const id = thread && thread.node && thread.node.id ? thread.node.id.toString() : null;
            if (id) {
                availableThreadIds.add(id);
            }
        });

        if (this.chatThreadExpandedState && this.chatThreadExpandedState.size > 0) {
            Array.from(this.chatThreadExpandedState).forEach(threadId => {
                if (!availableThreadIds.has(threadId)) {
                    this.chatThreadExpandedState.delete(threadId);
                }
            });
        }

        const focusThreadId = focusId ? this.findThreadIdContainingNode(threads, focusId) : null;
        if (focusThreadId) {
            this.chatThreadExpandedState.add(focusThreadId);
        }

        const fragment = document.createDocumentFragment();
        threads.forEach(threadNode => {
            const threadEl = this.createChatThreadElement(threadNode, activePathIds, focusId);
            if (threadEl) {
                fragment.appendChild(threadEl);
            }
        });

        this.chatTreeContainer.appendChild(fragment);

        const shouldRevealFocus = Boolean(focusId && focusId !== this.chatTreeLastFocusId);
        const maxScroll = Math.max(0, this.chatTreeContainer.scrollHeight - this.chatTreeContainer.clientHeight);

        if (shouldRevealFocus && focusId) {
            const focusSelector = `.chat-tree-item[data-node-id="${focusId}"]`;
            const focusEl = this.chatTreeContainer.querySelector(focusSelector);
            if (focusEl && typeof focusEl.scrollIntoView === 'function') {
                focusEl.scrollIntoView({ block: 'nearest' });
            }
            this.chatTreeScrollTop = this.chatTreeContainer.scrollTop;
        } else {
            const nextScroll = Math.min(previousScroll, maxScroll);
            this.chatTreeContainer.scrollTop = nextScroll;
            this.chatTreeScrollTop = nextScroll;
        }

        this.chatTreeLastFocusId = focusId || null;
    }

    createChatThreadElement(threadNode, activePathIds, focusId) {
        if (!threadNode || !threadNode.node) {
            return null;
        }

        const threadId = threadNode.node.id ? threadNode.node.id.toString() : null;
        const hasChildren = Array.isArray(threadNode.children) && threadNode.children.length > 0;
        const isExpanded = threadId ? this.chatThreadExpandedState.has(threadId) : true;
        const containsActive = this.treeContainsAnyFromSet(threadNode, activePathIds);
        const containsFocus = focusId ? this.treeContainsNodeId(threadNode, focusId) : false;
        const messageCount = this.countVisibleThreadMessages(threadNode);

        const threadEl = document.createElement('section');
        threadEl.className = 'chat-thread';
        if (containsActive || containsFocus) {
            threadEl.classList.add('is-active');
        }

        const headerBtn = document.createElement('button');
        headerBtn.type = 'button';
        headerBtn.className = 'chat-thread-toggle';
        headerBtn.setAttribute('role', 'treeitem');
        headerBtn.setAttribute('aria-level', '1');
        headerBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        if (isExpanded) {
            headerBtn.classList.add('is-expanded');
        }

        if (threadId) {
            headerBtn.dataset.threadId = threadId;
        }

        const bodyId = threadId ? `chatThreadBody-${threadId}` : null;
        if (bodyId) {
            headerBtn.setAttribute('aria-controls', bodyId);
        }

        if (threadId) {
            headerBtn.addEventListener('click', () => {
                this.toggleChatThread(threadId);
            });
        }

        const caret = document.createElement('span');
        caret.className = 'chat-thread-caret';
        caret.textContent = '▸';
        headerBtn.appendChild(caret);

        const title = document.createElement('span');
        title.className = 'chat-thread-title';
        title.textContent = this.formatChatTreeLabel(threadNode.node);
        headerBtn.appendChild(title);

        const meta = document.createElement('span');
        meta.className = 'chat-thread-meta';
        if (messageCount > 0) {
            meta.textContent = messageCount === 1 ? '1 message' : `${messageCount} messages`;
        } else {
            meta.textContent = 'No messages yet';
        }
        headerBtn.appendChild(meta);

        // Add delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'chat-thread-delete';
        deleteBtn.textContent = '×';
        deleteBtn.title = 'Delete this thread';
        deleteBtn.setAttribute('aria-label', 'Delete thread');
        if (threadId) {
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation(); // Prevent toggle when clicking delete
                this.deleteThread(threadId);
            });
        }
        headerBtn.appendChild(deleteBtn);

        threadEl.appendChild(headerBtn);

        const body = document.createElement('div');
        body.className = 'chat-thread-body';
        body.setAttribute('role', 'group');
        if (bodyId) {
            body.id = bodyId;
        }

        if (!isExpanded) {
            body.hidden = true;
        }

        if (hasChildren) {
            this.appendChatThreadChildren(body, threadNode.children, 1, activePathIds, focusId);
        } else {
            const empty = document.createElement('div');
            empty.className = 'chat-thread-empty';
            empty.textContent = 'This thread has no messages yet.';
            body.appendChild(empty);
        }

        threadEl.appendChild(body);

        if (containsFocus) {
            headerBtn.classList.add('is-active');
        }

        return threadEl;
    }

    appendChatThreadChildren(container, nodes, depth, activePathIds, focusId) {
        if (!container || !Array.isArray(nodes) || nodes.length === 0) {
            return;
        }

        nodes.forEach(treeNode => {
            if (!treeNode || !treeNode.node) {
                return;
            }

            const item = this.createChatTreeItem(treeNode, depth, activePathIds, focusId);
            if (item) {
                container.appendChild(item);
            }

            if (treeNode.children && treeNode.children.length > 0) {
                this.appendChatThreadChildren(container, treeNode.children, depth + 1, activePathIds, focusId);
            }
        });
    }

    toggleChatThread(threadId) {
        if (!threadId) {
            return;
        }

        const normalizedId = threadId.toString();
        if (this.chatThreadExpandedState.has(normalizedId)) {
            this.chatThreadExpandedState.delete(normalizedId);
        } else {
            this.chatThreadExpandedState.add(normalizedId);
        }

        if (this.chatTreeContainer) {
            this.chatTreeScrollTop = this.chatTreeContainer.scrollTop;
        }

        this.renderChatTree();
    }

    deleteThread(threadId) {
        if (!threadId || !this.canvasState) {
            return;
        }

        const normalizedId = threadId.toString();
        
        // Get all nodes in this thread
        const threadNodes = this.getNodesForThread(normalizedId);
        
        if (!threadNodes || threadNodes.length === 0) {
            console.warn('No nodes found for thread:', normalizedId);
            return;
        }

        // Confirm deletion
        const messageCount = threadNodes.filter(n => n && !n.hideChatMessage).length;
        const threadLabel = this.formatChatTreeLabel(threadNodes[0]) || 'this thread';
        
        if (!confirm(`Delete ${threadLabel} with ${messageCount} message${messageCount !== 1 ? 's' : ''}?`)) {
            return;
        }

        // Save state for undo
        this.canvasState.saveState();

        // Collect all node IDs to delete
        const nodeIdsToDelete = new Set(threadNodes.map(n => n.id));

        // Clean up video and iframe elements
        threadNodes.forEach(node => {
            if (node._videoObj) {
                try {
                    if (node._videoObj.parentNode) {
                        node._videoObj.parentNode.removeChild(node._videoObj);
                    }
                } catch (e) {
                    console.warn('Failed to remove video element:', e);
                }
                node._videoObj = null;
            }
            if (node._iframeObj) {
                try {
                    if (node._iframeObj.parentNode) {
                        node._iframeObj.parentNode.removeChild(node._iframeObj);
                    }
                } catch (e) {
                    console.warn('Failed to remove iframe element:', e);
                }
                node._iframeObj = null;
            }
        });

        // Remove nodes from spatial grid
        if (typeof this.canvasState.rebuildSpatialGrid === 'function') {
            threadNodes.forEach(node => {
                if (typeof this.canvasState.removeFromSpatialGrid === 'function') {
                    this.canvasState.removeFromSpatialGrid(node);
                }
            });
        }

        // Delete the nodes
        this.canvasState.nodes = this.canvasState.nodes.filter(node => !nodeIdsToDelete.has(node.id));

        // Delete connections associated with these nodes
        this.canvasState.connections = this.canvasState.connections.filter(conn => 
            !nodeIdsToDelete.has(conn.fromNode) && !nodeIdsToDelete.has(conn.toNode)
        );

        // Clear selection if any deleted nodes were selected
        this.canvasState.selectedNodes = this.canvasState.selectedNodes.filter(
            node => !nodeIdsToDelete.has(node.id)
        );

        // If this was the active thread, clear it
        if (this.canvasState.activeThreadId === normalizedId) {
            this.canvasState.setActiveThread(null);
            this.activeCanvasThreadId = null;
        }

        // Remove from expanded state
        this.chatThreadExpandedState.delete(normalizedId);

        // Rebuild spatial grid and save
        this.canvasState.rebuildSpatialGrid();
        this.canvasState.saveToLocalStorage();

        // Update UI
        this.updateUndoRedoButtons();
        this.renderChatTree();
        this.showNotification(`Thread deleted successfully`, 'success', 2000);

        console.log(`🗑️ Deleted thread ${normalizedId} with ${nodeIdsToDelete.size} nodes`);
    }

    countVisibleThreadMessages(treeNode) {
        if (!treeNode) {
            return 0;
        }

        const isVisibleNode = treeNode.node && !treeNode.node.hideChatMessage ? 1 : 0;
        const childTotal = Array.isArray(treeNode.children)
            ? treeNode.children.reduce((sum, child) => sum + this.countVisibleThreadMessages(child), 0)
            : 0;
        return isVisibleNode + childTotal;
    }

    findThreadIdContainingNode(threads, nodeId) {
        if (!Array.isArray(threads) || threads.length === 0 || !nodeId) {
            return null;
        }

        const target = nodeId.toString();
        for (const thread of threads) {
            if (!thread || !thread.node || !thread.node.id) {
                continue;
            }
            const threadId = thread.node.id.toString();
            if (this.treeContainsNodeId(thread, target)) {
                return threadId;
            }
        }

        return null;
    }

    treeContainsNodeId(treeNode, nodeId) {
        if (!treeNode || !nodeId) {
            return false;
        }

        const target = nodeId.toString();
        const currentId = treeNode.node && treeNode.node.id ? treeNode.node.id.toString() : null;
        if (currentId && currentId === target) {
            return true;
        }

        return Array.isArray(treeNode.children)
            ? treeNode.children.some(child => this.treeContainsNodeId(child, target))
            : false;
    }

    treeContainsAnyFromSet(treeNode, idSet) {
        if (!treeNode || !idSet || idSet.size === 0) {
            return false;
        }

        const nodeId = treeNode.node && treeNode.node.id ? treeNode.node.id.toString() : null;
        if (nodeId && idSet.has(nodeId)) {
            return true;
        }

        return Array.isArray(treeNode.children)
            ? treeNode.children.some(child => this.treeContainsAnyFromSet(child, idSet))
            : false;
    }

    buildChatThreads() {
        if (!this.canvasState || !Array.isArray(this.canvasState.nodes)) {
            this.chatThreadDiagnostics = {
                totalChatNodes: 0,
                connectionCount: Array.isArray(this.canvasState?.connections) ? this.canvasState.connections.length : 0,
                rootCount: 0,
                threadCount: 0
            };
            return [];
        }

        const chatNodes = this.canvasState.nodes.filter(node => node && node.isChatMessage);
        if (chatNodes.length === 0) {
            this.chatThreadDiagnostics = {
                totalChatNodes: 0,
                connectionCount: Array.isArray(this.canvasState.connections) ? this.canvasState.connections.length : 0,
                rootCount: 0,
                threadCount: 0
            };
            return [];
        }

        const nodeIdSet = new Set(chatNodes.map(node => (node && node.id ? node.id.toString() : null)).filter(Boolean));
        const childIdSet = new Set();

        (this.canvasState.connections || []).forEach(connection => {
            if (!connection || !connection.toNode) return;
            if (nodeIdSet.has(connection.toNode.toString())) {
                childIdSet.add(connection.toNode.toString());
            }
        });

        const roots = chatNodes
            .filter(node => node && node.id && !childIdSet.has(node.id.toString()))
            .sort((a, b) => this.sortChatNodes(a, b));

        const diagnostics = {
            totalChatNodes: chatNodes.length,
            connectionCount: Array.isArray(this.canvasState.connections) ? this.canvasState.connections.length : 0,
            rootCount: roots.length,
            threadCount: 0
        };

        const visited = new Set();
        const threads = roots
            .map(root => this.buildChatTreeNode(root, visited))
            .filter(Boolean);

        diagnostics.threadCount = threads.length;
        this.chatThreadDiagnostics = diagnostics;

        return threads;
    }

    buildChatTreeNode(node, visited = new Set()) {
        if (!node || !node.id) {
            return null;
        }

        const nodeId = node.id.toString();
        if (visited.has(nodeId)) {
            return null;
        }

        const nextVisited = new Set(visited);
        nextVisited.add(nodeId);

        const children = (this.getChildNodes(node) || []).map(child => this.buildChatTreeNode(child, nextVisited)).filter(Boolean);

        return {
            node,
            children,
            childCount: children.length
        };
    }

    sortChatNodes(a, b) {
        if (!a || !b) return 0;
        const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : Number.MAX_SAFE_INTEGER;
        const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : Number.MAX_SAFE_INTEGER;
        if (seqA !== seqB) return seqA - seqB;
        const yA = typeof a.y === 'number' ? a.y : 0;
        const yB = typeof b.y === 'number' ? b.y : 0;
        if (yA !== yB) return yA - yB;
        const xA = typeof a.x === 'number' ? a.x : 0;
        const xB = typeof b.x === 'number' ? b.x : 0;
        return xA - xB;
    }

    createChatTreeItem(treeNode, depth, activePathIds, focusId) {
        if (!treeNode || !treeNode.node) {
            return null;
        }

        const { node, children, childCount } = treeNode;
        const nodeId = node.id ? node.id.toString() : null;
        const isInPath = nodeId ? activePathIds.has(nodeId) : false;
        const isFocus = nodeId && focusId ? nodeId === focusId : false;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'chat-tree-item';
        const indentLevel = Number.isFinite(depth) ? depth : 0;
        button.style.setProperty('--indent-level', indentLevel.toString());
        button.setAttribute('role', 'treeitem');
        button.setAttribute('aria-level', (depth + 1).toString());

        if (nodeId) {
            button.dataset.nodeId = nodeId;
            button.setAttribute('aria-selected', isFocus ? 'true' : 'false');
        }

        if (isInPath) {
            button.classList.add('is-active');
        }

        if (isFocus) {
            button.classList.add('is-focused');
            button.setAttribute('aria-current', 'true');
        }

        if (node.hideChatMessage) {
            button.classList.add('is-thread');
            button.setAttribute('aria-expanded', children && children.length > 0 ? 'true' : 'false');
        }

        const icon = document.createElement('span');
        icon.className = 'chat-tree-item-icon';
        icon.textContent = this.getChatTreeIcon(node);
        button.appendChild(icon);

        const body = document.createElement('span');
        body.className = 'chat-tree-item-body';

        const title = document.createElement('span');
        title.className = 'chat-tree-item-title';
        title.textContent = this.formatChatTreeLabel(node);
        body.appendChild(title);

        const metaText = this.formatChatTreeMeta(node, childCount);
        if (metaText) {
            const meta = document.createElement('span');
            meta.className = 'chat-tree-item-meta';
            meta.textContent = metaText;
            body.appendChild(meta);
        }

        button.appendChild(body);

        const tooltipParts = [];
        const trimmedText = (node.text || '').trim();
        if (trimmedText) {
            tooltipParts.push(trimmedText);
        }
        if (node.generatedByModel) {
            tooltipParts.push(`Model: ${node.generatedByModel}`);
        }
        if (childCount > 0) {
            tooltipParts.push(childCount === 1 ? '1 linked message' : `${childCount} linked messages`);
        }
        if (tooltipParts.length > 0) {
            button.title = tooltipParts.join('\n');
        }

        if (nodeId) {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                this.handleChatTreeSelection(nodeId);
            });
        }

        return button;
    }

    formatChatTreeLabel(node) {
        if (!node) {
            return 'Message';
        }

        const rawText = (node.text || '').trim();
        if (node.hideChatMessage) {
            return rawText || 'Chat Thread';
        }

        const role = node.chatRole || (node.generatedByModel ? 'assistant' : 'user');
        if (!rawText) {
            return role === 'assistant' ? 'Assistant response' : role === 'user' ? 'User message' : 'Message';
        }

        const firstLine = rawText.split(/\r?\n/).find(line => line.trim().length > 0) || rawText;
        const maxLength = 80;
        return firstLine.length > maxLength ? `${firstLine.slice(0, maxLength - 1)}…` : firstLine;
    }

    formatChatTreeMeta(node, childCount = 0) {
        if (!node) {
            return '';
        }

        const parts = [];
        const normalizedCount = typeof childCount === 'number' && childCount > 0 ? childCount : 0;

        if (node.chatRole === 'assistant' && node.generatedByModel) {
            const modelName = this.simplifyModelName(node.generatedByModel);
            if (modelName) {
                parts.push(modelName);
            }
        }

        if (normalizedCount > 0) {
            if (node.hideChatMessage || node.chatRole === 'system') {
                parts.push(normalizedCount === 1 ? '1 message' : `${normalizedCount} messages`);
            } else if (node.chatRole === 'user') {
                parts.push(normalizedCount === 1 ? '1 reply' : `${normalizedCount} replies`);
            } else {
                parts.push(normalizedCount === 1 ? '1 follow-up' : `${normalizedCount} follow-ups`);
            }
        }

        return parts.join(' • ');
    }

    getChatTreeIcon(node) {
        if (!node) {
            return '🧩';
        }

        if (node.hideChatMessage) {
            return '🌿';
        }

        const role = node.chatRole || (node.generatedByModel ? 'assistant' : 'user');
        if (role === 'assistant') {
            return '🤖';
        }
        if (role === 'user') {
            return '🧑';
        }
        if (role === 'system') {
            return '⚙️';
        }
        return node.generatedByModel ? '🤖' : '📝';
    }

    simplifyModelName(modelName) {
        if (!modelName || typeof modelName !== 'string') {
            return '';
        }

        const trimmed = modelName.trim();
        if (!trimmed) {
            return '';
        }

        const parts = trimmed.split('/');
        const lastPart = parts[parts.length - 1] || trimmed;
        const maxLength = 32;
        return lastPart.length > maxLength ? `${lastPart.slice(0, maxLength - 1)}…` : lastPart;
    }

    handleChatTreeSelection(nodeId) {
        if (!nodeId || !this.canvasState || !Array.isArray(this.canvasState.nodes)) {
            return;
        }

        if (this.chatFocusNodeId && this.chatFocusNodeId.toString() === nodeId.toString() && this.currentViewMode === 'chat') {
            return;
        }

        const targetNode = this.canvasState.nodes.find(node => node && node.id && node.id.toString() === nodeId.toString());
        if (!targetNode) {
            return;
        }

        this.enterChatForNode(targetNode);
    }

    getMessageNavigationInfo(nodeId) {
        if (!nodeId || !this.canvasState) {
            return null;
        }

        // Find the node in canvas state
        const node = this.getChatNodeById(nodeId);
        if (!node || node.chatRole !== 'assistant') {
            console.debug('[ChatNav] Navigation unavailable: node missing or not assistant', { nodeId, role: node ? node.chatRole : null });
            return null;
        }

        // Get parent node to find siblings
        const parent = this.getParentNode(node);
        if (!parent) {
            console.debug('[ChatNav] Navigation unavailable: no parent node', { nodeId });
            return null;
        }

        // Get all siblings (children of the parent)
        const siblings = this.getChildNodes(parent);
        if (!siblings || siblings.length <= 1) {
            console.debug('[ChatNav] Navigation unavailable: insufficient siblings', {
                nodeId,
                parentId: parent.id,
                siblingCount: siblings ? siblings.length : 0,
                siblingIds: siblings ? siblings.map(s => s.id) : []
            });
            return null;
        }

        // Find current position among siblings
        const siblingIndex = siblings.findIndex(sibling => sibling.id === node.id);
        if (siblingIndex === -1) {
            console.debug('[ChatNav] Navigation unavailable: node not found among siblings', {
                nodeId,
                parentId: parent.id,
                siblingIds: siblings.map(s => s.id)
            });
            return null;
        }

        const navInfo = {
            siblings: siblings,
            position: siblingIndex + 1,
            total: siblings.length,
            currentIndex: siblingIndex
        };
        console.debug('[ChatNav] Navigation info resolved', {
            nodeId,
            parentId: parent.id,
            siblingIds: siblings.map(s => s.id),
            position: siblingIndex + 1,
            total: siblings.length
        });
        return navInfo;
    }

    navigateToMessageSibling(nodeId, direction) {
        if (!nodeId || !this.chatBranchNodes || this.chatBranchNodes.length === 0) {
            return;
        }

        const nodeIndex = this.chatBranchNodes.findIndex(node =>
            node && node.id && node.id.toString() === nodeId.toString()
        );

        if (nodeIndex === -1) {
            return;
        }

        this.navigateBranchLevel(nodeIndex, direction);
    }

    navigateBranchLevel(levelIndex, direction) {
        if (!this.chatBranchLevels || !this.chatBranchLevels[levelIndex]) {
            return;
        }

        const level = this.chatBranchLevels[levelIndex];
        const siblings = level.siblings || [];
        if (siblings.length <= 1) {
            return;
        }

        const currentIndex = level.siblingIndex ?? 0;
        let nextIndex = currentIndex + direction;
        if (nextIndex < 0) nextIndex = siblings.length - 1;
        if (nextIndex >= siblings.length) nextIndex = 0;

        const newNode = siblings[nextIndex];
        if (!newNode) return;

        const prefix = this.chatBranchNodes.slice(0, levelIndex);
        const newBranch = [...prefix, newNode];
        const descendantChain = this.buildDescendantChain(newNode);
        descendantChain.slice(1).forEach(child => {
            newBranch.push(child);
        });

        this.chatBranchNodes = newBranch;
        this.chatFocusNodeId = newBranch[newBranch.length - 1]?.id || null;
        this.updateBranchState({ preserveBase: levelIndex !== 0 });
        this.renderChatFromBranch();
    }

    buildBranchFromNode(node) {
        const ancestors = this.buildAncestorChain(node);
        const descendantChain = this.buildDescendantChain(node);
        const fullBranch = ancestors.concat(descendantChain.slice(1));
        return fullBranch;
    }

    buildAncestorChain(node) {
        const chain = [];
        let current = node;
        const safeGuard = new Set();

        while (current && !safeGuard.has(current.id)) {
            chain.unshift(current);
            safeGuard.add(current.id);
            current = this.getParentNode(current);
        }

        return chain;
    }

    buildDescendantChain(startNode) {
        const chain = [];
        let current = startNode;
        const visited = new Set();

        while (current && !visited.has(current.id)) {
            chain.push(current);
            visited.add(current.id);

            const children = this.getChildNodes(current);
            if (!children || children.length === 0) {
                break;
            }

            children.sort((a, b) => {
                const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : Number.MAX_SAFE_INTEGER;
                const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : Number.MAX_SAFE_INTEGER;
                if (seqA !== seqB) return seqA - seqB;
                return (a.y || 0) - (b.y || 0);
            });

            current = children[0];
        }

        return chain;
    }

    convertNodeToChatMessage(node, index) {
        if (!node || node.hideChatMessage) return null;

        const text = (node.text || '').trim();
        if (text.length === 0) return null;

        const baseRole = node.chatRole || (node.generatedByModel ? 'assistant' : 'user');

        if (index === 0 && !node.chatRole) {
            return {
                role: 'system',
                content: text
            };
        }

        return {
            role: node.chatRole || baseRole,
            content: text,
            model: node.generatedByModel || null,
            nodeId: node.id ? node.id.toString() : null,
            sequence: typeof node.chatSequence === 'number' ? node.chatSequence : index
        };
    }

    syncChatMessageToCanvas(role, content, { model = null, select = true } = {}) {
        if (!this.canvasState) return null;

        if (!this.chatBranchNodes || this.chatBranchNodes.length === 0) {
            const ensuredRoot = this.ensureActiveChatBranch({ reason: 'message-submit-no-branch' });
            if (!ensuredRoot) {
                return null;
            }
        }

        let parentNode = this.lastChatNode || this.chatBranchNodes[this.chatBranchNodes.length - 1];
        if (role === 'assistant') {
            const latestUserNode = [...this.chatBranchNodes].reverse().find(node => node && node.chatRole === 'user');
            if (latestUserNode) {
                parentNode = latestUserNode;
            }
        }

        if (!parentNode) {
            return null;
        }

        const generatedByModel = role === 'assistant'
            ? (model || (this.aiManager ? this.aiManager.getPrimaryTextModel() : null))
            : null;

        const nodeX = this.chatNodeBaseX + (role === 'user' ? this.chatUserOffsetX : this.chatAssistantOffsetX);
        const parentY = typeof parentNode.y === 'number' ? parentNode.y : this.chatNodeBaseY + ((this.chatBranchNodes.length - 1) * this.chatNodeVerticalSpacing);
        const nodeY = parentY + this.chatNodeVerticalSpacing;

        const threadRoot = this.findThreadRoot(parentNode);
        const threadId = threadRoot && threadRoot.id ? threadRoot.id.toString() : (this.canvasState.activeThreadId || null);

        const newNode = this.canvasState.createNode(content, nodeX, nodeY, generatedByModel, { save: false, threadId });
        newNode.isChatMessage = true;
        newNode.chatRole = role;
        newNode.chatSequence = this.chatMessageIndex++;
        newNode.width = 360;
        const extraHeight = Math.max(0, content.length - 180) * 0.5;
        newNode.height = Math.min(140 + extraHeight, 360);

        this.canvasState.createConnection(parentNode, newNode);

        if (role === 'assistant') {
            if (select) {
                const parentIndex = this.chatBranchNodes.findIndex(node => node.id === parentNode.id);
                if (parentIndex >= 0) {
                    this.chatBranchNodes = this.chatBranchNodes.slice(0, parentIndex + 1);
                }
                this.chatBranchNodes.push(newNode);
                this.lastChatNode = newNode;
                this.chatFocusNodeId = newNode.id;
            }
        } else {
            this.chatBranchNodes.push(newNode);
            this.lastChatNode = newNode;
            this.chatFocusNodeId = newNode.id;
        }

        this.canvasState.saveToLocalStorage();
        if (typeof this.updateUndoRedoButtons === 'function') {
            this.updateUndoRedoButtons();
        }

        return newNode;
    }

    getParentNode(node) {
        if (!node || !this.canvasState) return null;
        const nodeId = node.id.toString();
        const parentConnection = this.canvasState.connections.find(conn => conn.toNode === nodeId);
        if (!parentConnection) return null;
        return this.canvasState.nodes.find(n => n.id.toString() === parentConnection.fromNode) || null;
    }

    getChildNodes(node) {
        if (!node || !this.canvasState) return [];
        const nodeId = node.id.toString();
        const children = this.canvasState.connections
            .filter(conn => conn.fromNode === nodeId)
            .map(conn => this.canvasState.nodes.find(n => n.id.toString() === conn.toNode))
            .filter(Boolean);

        children.sort((a, b) => {
            const seqA = typeof a.chatSequence === 'number' ? a.chatSequence : Number.MAX_SAFE_INTEGER;
            const seqB = typeof b.chatSequence === 'number' ? b.chatSequence : Number.MAX_SAFE_INTEGER;
            if (seqA !== seqB) return seqA - seqB;
            return (a.y || 0) - (b.y || 0);
        });

        return children;
    }

    updateChatModelInfo() {
        if (!this.chatModelInfoEl) {
            this.chatModelInfoEl = document.getElementById('chatModelInfo');
        }

        if (!this.chatModelInfoEl) return;

        this.chatModelInfoEl.innerHTML = '';

        if (!this.aiManager) {
            this.chatModelInfoEl.textContent = 'Models unavailable';
            return;
        }

        const providerName = this.aiManager.getProviderName();
        const isAutomanaged = this.aiManager.automanaged;
        const descriptors = this.aiManager.getTextModelDescriptors();
        const activeDescriptors = this.aiManager.getActiveTextModelDescriptors();

        const primaryDescriptor = activeDescriptors.length > 0
            ? activeDescriptors[0]
            : (descriptors.length > 0 ? descriptors[0] : null);

        const addPill = (label) => {
            const pill = document.createElement('span');
            pill.className = 'chat-model-pill';
            pill.textContent = label;
            this.chatModelInfoEl.appendChild(pill);
        };

        if (isAutomanaged) {
            addPill('Automanaged');
        }

        if (providerName) {
            addPill(`Provider: ${providerName}`);
        }

        if (primaryDescriptor) {
            addPill(`Primary: ${primaryDescriptor.label || primaryDescriptor.name}`);
        }

        if (activeDescriptors.length === 0) {
            addPill('No active models');
        } else {
            const maxModelsToShow = 3;
            activeDescriptors.slice(0, maxModelsToShow).forEach(descriptor => {
                if (!primaryDescriptor || descriptor.key !== primaryDescriptor.key) {
                    addPill(descriptor.label || descriptor.name);
                }
            });

            if (activeDescriptors.length > maxModelsToShow) {
                addPill(`+${activeDescriptors.length - maxModelsToShow} more`);
            }
        }
    }

    showAspectRatioSelector(button) {
        if (this.aspectRatioTimeout) {
            clearTimeout(this.aspectRatioTimeout);
            this.aspectRatioTimeout = null;
        }

        const aspectRatioSelector = document.getElementById('aspectRatioSelector');
        if (!aspectRatioSelector) return;

        // Position the selector above the button with minimal gap
        const buttonRect = button.getBoundingClientRect();
        aspectRatioSelector.style.left = buttonRect.left + 'px';
        aspectRatioSelector.style.top = (buttonRect.top - 50) + 'px'; // Reduced gap from 60px to 50px

        aspectRatioSelector.classList.remove('hidden');
        this.aspectRatioSelectorVisible = true;
    }

    hideAspectRatioSelector() {
        const aspectRatioSelector = document.getElementById('aspectRatioSelector');
        if (!aspectRatioSelector) return;

        aspectRatioSelector.classList.add('hidden');
        this.aspectRatioSelectorVisible = false;

        if (this.aspectRatioTimeout) {
            clearTimeout(this.aspectRatioTimeout);
            this.aspectRatioTimeout = null;
        }
    }

    getSelectedAspectRatio() {
        return this.selectedAspectRatio;
    }

    // ===========================================
    // Authentication UI Methods
    // ===========================================

    setupAuthUI() {
        // Create auth modal
        this.createAuthModal();
        
        // Create sign in button and user menu in header
        this.createAuthHeaderUI();
        
        // Create guest signup prompt banner
        this.createGuestPromptBanner();
    }

    createAuthModal() {
        if (document.getElementById('authModal')) return;

        const modal = document.createElement('div');
        modal.id = 'authModal';
        modal.className = 'auth-modal';
        modal.innerHTML = `
            <div class="auth-modal-content">
                <button class="auth-modal-close" id="authModalClose">×</button>
                
                <div class="auth-modal-header">
                    <h2 id="authModalTitle">Sign In</h2>
                    <p id="authModalSubtitle">Save your canvases and access them anywhere</p>
                </div>

                <div class="auth-modal-body">
                    <!-- Email/Password Form -->
                    <form id="authForm" class="auth-form">
                        <div class="auth-form-group">
                            <label for="authEmail">Email</label>
                            <input type="email" id="authEmail" placeholder="you@example.com" required />
                        </div>
                        <div class="auth-form-group">
                            <label for="authPassword">Password</label>
                            <input type="password" id="authPassword" placeholder="••••••••" required />
                        </div>
                        <div id="authError" class="auth-error"></div>
                        <button type="submit" class="auth-submit-btn" id="authSubmitBtn">
                            Sign In
                        </button>
                    </form>

                    <!-- Toggle between sign in / sign up -->
                    <div class="auth-toggle">
                        <span id="authToggleText">Don't have an account?</span>
                        <button type="button" class="auth-toggle-btn" id="authToggleBtn">Sign Up</button>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Event listeners
        document.getElementById('authModalClose').addEventListener('click', () => this.hideAuthModal());
        document.getElementById('authForm').addEventListener('submit', (e) => this.handleAuthFormSubmit(e));
        document.getElementById('authToggleBtn').addEventListener('click', () => this.toggleAuthMode());
        
        // Close modal when clicking outside
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.hideAuthModal();
            }
        });

        this.authModalEl = modal;
    }

    createAuthHeaderUI() {
        // Don't create if already exists
        if (document.getElementById('authHeaderContainer')) {
            return;
        }

        const header = document.querySelector('.chat-header');
        if (!header) {
            console.warn('Header not found for auth UI');
            return;
        }

        // Create container for auth UI
        const authContainer = document.createElement('div');
        authContainer.id = 'authHeaderContainer';
        authContainer.className = 'auth-header-container';

        // Sign in button (shown when not authenticated)
        const signInBtn = document.createElement('button');
        signInBtn.id = 'authSignInBtn';
        signInBtn.className = 'auth-sign-in-btn';
        signInBtn.textContent = '🔐 Sign In';
        signInBtn.addEventListener('click', () => this.showAuthModal('signin'));

        // User menu (shown when authenticated)
        const userMenu = document.createElement('div');
        userMenu.id = 'authUserMenu';
        userMenu.className = 'auth-user-menu hidden';
        userMenu.innerHTML = `
            <button class="auth-user-btn" id="authUserBtn" type="button">
                <div class="auth-user-avatar" id="authUserAvatar">U</div>
                <span class="auth-user-name" id="authUserName">User</span>
            </button>
            <div class="auth-dropdown-menu hidden" id="authDropdownMenu">
                <div class="auth-dropdown-user-info">
                    <div class="auth-dropdown-name" id="authDropdownName">User</div>
                    <div class="auth-dropdown-email" id="authDropdownEmail">user@email.com</div>
                </div>
                <div class="auth-dropdown-divider"></div>
                <button class="auth-dropdown-item" id="authSignOutBtn" type="button">
                    <span>🚪 Sign Out</span>
                </button>
            </div>
        `;

        authContainer.appendChild(signInBtn);
        authContainer.appendChild(userMenu);
        header.appendChild(authContainer);

        this.signInBtnEl = signInBtn;
        this.userMenuEl = userMenu;

        // User menu interactions
        const userBtn = document.getElementById('authUserBtn');
        const dropdownMenu = document.getElementById('authDropdownMenu');
        const signOutBtn = document.getElementById('authSignOutBtn');

        if (userBtn && dropdownMenu) {
            userBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                dropdownMenu.classList.toggle('hidden');
            });

            // Close dropdown when clicking outside
            document.addEventListener('click', () => {
                if (!dropdownMenu.classList.contains('hidden')) {
                    dropdownMenu.classList.add('hidden');
                }
            });
        }

        if (signOutBtn) {
            signOutBtn.addEventListener('click', () => {
                this.handleSignOut();
                // Close dropdown after sign out
                if (dropdownMenu) {
                    dropdownMenu.classList.add('hidden');
                }
            });
        }
    }

    createGuestPromptBanner() {
        if (document.getElementById('guestPromptBanner')) return;

        const banner = document.createElement('div');
        banner.id = 'guestPromptBanner';
        banner.className = 'guest-prompt-banner hidden';
        banner.innerHTML = `
            <div class="guest-prompt-content">
                <span class="guest-prompt-text">💡 Sign up to save your work and access it anywhere!</span>
                <button class="guest-prompt-btn" id="guestPromptSignUpBtn">Sign Up</button>
                <button class="guest-prompt-close" id="guestPromptClose">×</button>
            </div>
        `;

        document.body.appendChild(banner);

        document.getElementById('guestPromptSignUpBtn').addEventListener('click', () => {
            this.hideGuestPrompt();
            this.showAuthModal('signup');
        });

        document.getElementById('guestPromptClose').addEventListener('click', () => {
            this.hideGuestPrompt();
        });
    }

    showAuthModal(mode = 'signin') {
        if (!this.authManager || !this.authManager.isConfigured()) {
            this.showNotification('⚠️ Authentication not configured. Please add Supabase credentials to your .env file.', 'error');
            return;
        }

        this.currentAuthMode = mode;
        this.updateAuthModalMode();
        
        if (this.authModalEl) {
            this.authModalEl.classList.add('active');
            
            // Focus email input
            setTimeout(() => {
                const emailInput = document.getElementById('authEmail');
                if (emailInput) emailInput.focus();
            }, 100);
        }
    }

    hideAuthModal() {
        if (this.authModalEl) {
            this.authModalEl.classList.remove('active');
            
            // Clear form
            const form = document.getElementById('authForm');
            if (form) form.reset();
            
            // Clear error
            this.clearAuthError();
        }
    }

    updateAuthModalMode() {
        const title = document.getElementById('authModalTitle');
        const subtitle = document.getElementById('authModalSubtitle');
        const submitBtn = document.getElementById('authSubmitBtn');
        const toggleText = document.getElementById('authToggleText');
        const toggleBtn = document.getElementById('authToggleBtn');

        if (this.currentAuthMode === 'signin') {
            if (title) title.textContent = 'Sign In';
            if (subtitle) subtitle.textContent = 'Welcome back! Sign in to access your canvases';
            if (submitBtn) submitBtn.textContent = 'Sign In';
            if (toggleText) toggleText.textContent = "Don't have an account?";
            if (toggleBtn) toggleBtn.textContent = 'Sign Up';
        } else {
            if (title) title.textContent = 'Create Account';
            if (subtitle) subtitle.textContent = 'Sign up to save your canvases and access them anywhere';
            if (submitBtn) submitBtn.textContent = 'Sign Up';
            if (toggleText) toggleText.textContent = 'Already have an account?';
            if (toggleBtn) toggleBtn.textContent = 'Sign In';
        }
    }

    toggleAuthMode() {
        this.currentAuthMode = this.currentAuthMode === 'signin' ? 'signup' : 'signin';
        this.updateAuthModalMode();
        this.clearAuthError();
    }

    async handleAuthFormSubmit(e) {
        e.preventDefault();
        
        const email = document.getElementById('authEmail').value;
        const password = document.getElementById('authPassword').value;
        const submitBtn = document.getElementById('authSubmitBtn');

        if (!email || !password) {
            this.showAuthError('Please enter both email and password');
            return;
        }

        // Disable button during submission
        submitBtn.disabled = true;
        submitBtn.textContent = this.currentAuthMode === 'signin' ? 'Signing in...' : 'Signing up...';

        try {
            let result;
            if (this.currentAuthMode === 'signin') {
                result = await this.authManager.signInWithEmail(email, password);
            } else {
                result = await this.authManager.signUpWithEmail(email, password);
            }

            if (result.success) {
                this.hideAuthModal();
                const message = this.currentAuthMode === 'signin' 
                    ? '✅ Signed in successfully!' 
                    : '✅ Account created! Please check your email to verify.';
                this.showNotification(message, 'success');
            } else {
                this.showAuthError(result.error || 'Authentication failed');
            }
        } catch (error) {
            this.showAuthError(error.message || 'An error occurred');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = this.currentAuthMode === 'signin' ? 'Sign In' : 'Sign Up';
        }
    }

    async signInWithProvider(provider) {
        if (!this.authManager || !this.authManager.isConfigured()) {
            this.showNotification('⚠️ Authentication not configured', 'error');
            return;
        }

        try {
            const result = await this.authManager.signInWithProvider(provider);
            if (!result.success) {
                this.showAuthError(result.error || `${provider} sign in failed`);
            }
            // Note: OAuth will redirect, so we don't need to handle success here
        } catch (error) {
            this.showAuthError(error.message || 'OAuth sign in failed');
        }
    }

    async handleSignOut() {
        if (!this.authManager) return;

        try {
            const result = await this.authManager.signOut();
            if (result.success) {
                this.showNotification('👋 Signed out successfully', 'success');
            }
        } catch (error) {
            this.showNotification('Failed to sign out', 'error');
        }
    }

    handleAuthStateChange(event, session) {
        console.log('Auth state changed:', event, session?.user?.email);

        if (session && session.user) {
            // User is signed in
            this.updateUserUI(session.user);
            this.hideGuestPrompt();
        } else {
            // User is signed out
            this.updateUserUI(null);
        }
    }

    updateUserUI(user) {
        console.log('Updating user UI:', user ? user.email : 'logged out');

        if (user) {
            // User is logged in - show user menu, hide sign in button
            if (this.signInBtnEl) {
                this.signInBtnEl.classList.add('hidden');
            }
            if (this.userMenuEl) {
                this.userMenuEl.classList.remove('hidden');
            }

            // Update user info
            const userName = document.getElementById('authUserName');
            const dropdownName = document.getElementById('authDropdownName');
            const dropdownEmail = document.getElementById('authDropdownEmail');
            const userAvatar = document.getElementById('authUserAvatar');

            const displayName = this.authManager ? this.authManager.getUserDisplayName() : user.email;
            const avatarUrl = this.authManager ? this.authManager.getUserAvatarUrl() : null;

            // Update display name
            if (userName) {
                userName.textContent = displayName || user.email || 'User';
            }
            
            if (dropdownName) {
                dropdownName.textContent = displayName || user.email || 'User';
            }

            // Update email in dropdown
            if (dropdownEmail) {
                dropdownEmail.textContent = user.email || '';
            }

            // Update avatar
            if (userAvatar) {
                if (avatarUrl) {
                    userAvatar.style.backgroundImage = `url(${avatarUrl})`;
                    userAvatar.textContent = '';
                } else {
                    // Show first letter of name or email
                    userAvatar.style.backgroundImage = 'none';
                    const letter = (displayName || user.email || 'U').charAt(0).toUpperCase();
                    userAvatar.textContent = letter;
                }
            }
            
            console.log('✅ User menu should now be visible');
        } else {
            // User is logged out - show sign in button, hide user menu
            if (this.signInBtnEl) {
                this.signInBtnEl.classList.remove('hidden');
            }
            if (this.userMenuEl) {
                this.userMenuEl.classList.add('hidden');
            }
            
            console.log('✅ Sign in button should now be visible');
        }
    }

    showAuthError(message) {
        const errorEl = document.getElementById('authError');
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
        }
    }

    clearAuthError() {
        const errorEl = document.getElementById('authError');
        if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
        }
    }

    // Show guest prompt after user creates several nodes
    checkGuestPrompt() {
        if (this.authManager && this.authManager.isAuthenticated()) {
            return; // User is already signed in
        }

        this.nodesCreatedAsGuest++;

        // Show prompt after 5 nodes, with cooldown
        if (this.nodesCreatedAsGuest >= 5) {
            const now = Date.now();
            if (now - this.lastGuestPromptTime > this.guestPromptCooldown) {
                this.showGuestPrompt();
                this.lastGuestPromptTime = now;
                this.nodesCreatedAsGuest = 0; // Reset counter
            }
        }
    }

    showGuestPrompt() {
        const banner = document.getElementById('guestPromptBanner');
        if (banner) {
            banner.classList.remove('hidden');
            
            // Auto-hide after 10 seconds
            setTimeout(() => {
                this.hideGuestPrompt();
            }, 10000);
        }
    }

    hideGuestPrompt() {
        const banner = document.getElementById('guestPromptBanner');
        if (banner) {
            banner.classList.add('hidden');
        }
    }
} 
